import {
  AGES, BUILDING_COSTS, DEFAULT_MAP_SIZE, EXPEDITION_QUESTS, EXPLORATION_RULES, FACTIONS, FAUNA_PROFILES, FORTIFICATION_LEVELS, GUARDIAN_PROFILES, HEROES, HOME_RESOURCE_LAYOUT, MAP_SIZES, NEARBY_RESOURCE_LAYOUT, PRODUCTION_RULES, RESEARCH, RESOURCE_ENCLAVE_RULES, RESOURCE_NODE_RADII, RESOURCE_TYPES, TECHNOLOGIES, TRADE_RULES, WORKER_JOBS, WORLD,
  ageModifiers, buildingProfile, canAfford, spend, unitProfile, unlockAge,
} from './data.js';
const WELL_HOLD_SECONDS = 75;
const TAU = Math.PI * 2;
const MAX_COMMAND_LOG = 180;
const MAX_EVENTS = 80;
const GATHERERS_PER_NODE = WORKER_JOBS.gather.maxSlots;
const DEFEND_RADIUS = 150;
const GUARDIAN_CAMP_LEASH_PADDING = 200;
const BUILD_WORK_SLOTS = 8;
const PATROL_ARRIVAL_RADIUS = 8;
const explorationCaches = new WeakMap();

// These retired quick-map definitions remain simulation-internal so existing
// saves and fast unit scenarios still hydrate deterministically. Only the
// three MAP_SIZES exports are presented as new-match choices.
const LEGACY_MAPS = Object.freeze({
  frontier: Object.freeze({ id: 'frontier', name: 'Frontier Basin (Legacy)', theme: 'legacy-temperate', biomeIdentity: 'legacy-basin', width: 1600, height: 1000, camps: 3, resourceScale: 0.8 }),
  'expanse-v2': Object.freeze({ id: 'expanse', name: 'Shardmeadow Expanse (Legacy)', theme: 'legacy-temperate', biomeIdentity: 'legacy-expanse', width: 2400, height: 1500, camps: 5, resourceScale: 1.25 }),
  dominion: Object.freeze({ id: 'dominion', name: 'Dominion Reach (Legacy)', theme: 'legacy-temperate', biomeIdentity: 'legacy-dominion', width: 3200, height: 2000, camps: 7, resourceScale: 1.7 }),
});

function rng(seed) {
  let value = (seed >>> 0) || 0x9e3779b9;
  return () => { value ^= value << 13; value ^= value >>> 17; value ^= value << 5; return (value >>> 0) / 4294967296; };
}

function id(state, prefix) { return `${prefix}-${state.nextId++}`; }
function distance(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }
function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }
function alive(item) { return item && item.hp > 0; }
function factionOf(state, factionId) { return state.factions.find(item => item.id === factionId); }
function worldOf(state) { return state.world || WORLD; }
function teamOf(state, factionId) { const faction = factionOf(state, factionId); return faction && state.teams?.find(team => team.id === faction.teamId); }

function explorationGrid(world) {
  const cellSize = EXPLORATION_RULES.cellSize;
  const columns = Math.ceil(world.width / cellSize), rows = Math.ceil(world.height / cellSize);
  return { cellSize, columns, rows, maxCells: columns * rows };
}

function normalizedExploredCells(cells, maxCells) {
  if (!Array.isArray(cells)) return [];
  return [...new Set(cells.filter(cell => Number.isInteger(cell) && cell >= 0 && cell < maxCells))].sort((left, right) => left - right);
}

function explorationSet(state, team) {
  let stateCache = explorationCaches.get(state);
  if (!stateCache) { stateCache = new Map(); explorationCaches.set(state, stateCache); }
  const cached = stateCache.get(team.id);
  if (cached && cached.cells === team.exploredCells && cached.size === team.exploredCells.length) return cached.set;
  const set = new Set(team.exploredCells); stateCache.set(team.id, { cells: team.exploredCells, size: team.exploredCells.length, set });
  return set;
}

/**
 * Renderer-facing persistent exploration contract. Cell ids are sorted,
 * unique row-major integers: row * columns + column. Missing ids have never
 * been explored by that faction; this list never shrinks during a match.
 */
export function exploredCellsForFaction(state, factionId) {
  const grid = state.exploration || explorationGrid(worldOf(state));
  const faction = factionOf(state, factionId);
  return { ...grid, cells: faction?.exploredCells || [] };
}

// Compatibility summary for team strategy and old callers. Rendering never
// consumes this union: the player's fog uses exploredCellsForFaction instead.
export function exploredCellsForTeam(state, teamId) {
  const grid = state.exploration || explorationGrid(worldOf(state));
  const team = state.teams?.find(item => item.id === teamId);
  const cells = new Set();
  for (const factionId of team?.memberIds || []) for (const cell of exploredCellsForFaction(state, factionId).cells) cells.add(cell);
  return { ...grid, cells: [...cells].sort((left, right) => left - right) };
}

function updateExploredCells(state) {
  const grid = state.exploration || explorationGrid(worldOf(state));
  state.exploration = grid;
  const world = worldOf(state), { cellSize, columns, rows, maxCells } = grid;
  for (const faction of state.factions || []) {
    faction.exploredCells ||= [];
    if (faction.defeated || faction.exploredCells.length >= maxCells) continue;
    const explored = explorationSet(state, faction);
    let changed = false;
    const scouts = state.units.filter(unit => alive(unit) && unit.faction === faction.id);
    for (const scout of scouts) {
      const sight = Math.max(0, Number(scout.sight) || 0);
      const minColumn = clamp(Math.floor((scout.x - sight) / cellSize), 0, columns - 1);
      const maxColumn = clamp(Math.floor((scout.x + sight) / cellSize), 0, columns - 1);
      const minRow = clamp(Math.floor((scout.y - sight) / cellSize), 0, rows - 1);
      const maxRow = clamp(Math.floor((scout.y + sight) / cellSize), 0, rows - 1);
      const sightSquared = sight * sight;
      for (let row = minRow; row <= maxRow; row++) {
        const top = row * cellSize, bottom = Math.min(world.height, top + cellSize);
        for (let column = minColumn; column <= maxColumn; column++) {
          const left = column * cellSize, right = Math.min(world.width, left + cellSize);
          const nearestX = clamp(scout.x, left, right), nearestY = clamp(scout.y, top, bottom);
          if ((scout.x - nearestX) ** 2 + (scout.y - nearestY) ** 2 > sightSquared) continue;
          const cell = row * columns + column;
          if (!explored.has(cell)) { explored.add(cell); faction.exploredCells.push(cell); changed = true; }
        }
      }
    }
    if (changed) {
      faction.exploredCells.sort((left, right) => left - right);
      const stateCache = explorationCaches.get(state);
      if (stateCache) stateCache.set(faction.id, { cells: faction.exploredCells, size: faction.exploredCells.length, set: explored });
    }
  }
}

export function areAllies(state, leftFactionId, rightFactionId) {
  if (!leftFactionId || !rightFactionId || leftFactionId === 'neutral' || rightFactionId === 'neutral') return false;
  if (leftFactionId === rightFactionId) return true;
  const left = factionOf(state, leftFactionId), right = factionOf(state, rightFactionId);
  return Boolean(left && right && left.teamId && left.teamId === right.teamId);
}

export function areEnemies(state, leftFactionId, rightFactionId) {
  if (!leftFactionId || !rightFactionId || leftFactionId === rightFactionId) return false;
  if (leftFactionId === 'neutral' || rightFactionId === 'neutral') return true;
  return !areAllies(state, leftFactionId, rightFactionId);
}

export function factionCurrentlySees(state, factionId, point) {
  if (!Number.isFinite(point?.x) || !Number.isFinite(point?.y)) return false;
  for (const unit of state.units || []) if (alive(unit) && unit.faction === factionId && distance(unit, point) <= (unit.sight || 180)) return true;
  for (const building of state.buildings || []) if (alive(building) && building.faction === factionId && distance(building, point) <= (building.type === 'tower' ? 285 : 215)) return true;
  return false;
}

function researchModifiers(state, factionId) {
  const faction = factionOf(state, factionId), result = {
    unitHp: 1, unitDamage: 1, unitSpeed: 1, unitRange: 1, unitSight: 1, unitArmor: 0,
    buildingHp: 1, buildingArmor: 0, gatherRate: 1, buildSpeed: 1, repairRate: 1,
    trainSpeed: 1, captureRate: 1, towerDamage: 1, towerRange: 1, heroCooldown: 1,
    influence: 1, tradeRate: 1, knowledgeRate: 1, huntYield: 1,
  };
  const improvements = [
    ...(faction?.researches || []).map(researchId => RESEARCH[researchId]),
    ...(faction?.technologies || []).map(technologyId => TECHNOLOGIES[technologyId]),
  ].filter(Boolean);
  for (const improvement of improvements) {
    for (const [key, value] of Object.entries(improvement.effects || {})) {
      if (key.endsWith('Armor')) result[key] += value;
      else if (result[key] !== undefined) result[key] *= value;
    }
  }
  return result;
}

function effectiveUnitProfile(state, factionId, role) {
  const faction = factionOf(state, factionId), profile = unitProfile(factionId, role, faction?.age || 1), research = researchModifiers(state, factionId);
  return { ...profile, hp: Math.round(profile.hp * research.unitHp), damage: profile.damage * research.unitDamage, speed: profile.speed * research.unitSpeed, range: profile.range * research.unitRange, sight: profile.sight * research.unitSight, gatherRate: (profile.gatherRate || 0) * research.gatherRate, trainTime: profile.trainTime / research.trainSpeed, armor: research.unitArmor };
}

function effectiveBuildingProfile(state, factionId, type, fortificationLevel = 0) {
  const faction = factionOf(state, factionId), profile = buildingProfile(factionId, type, faction?.age || 1), research = researchModifiers(state, factionId);
  let fortificationHp = 1, fortificationArmor = 0;
  for (let level = 1; level <= fortificationLevel; level++) { fortificationHp *= FORTIFICATION_LEVELS[level]?.hpMultiplier || 1; fortificationArmor += FORTIFICATION_LEVELS[level]?.armor || 0; }
  return { ...profile, hp: Math.round(profile.hp * research.buildingHp * fortificationHp), armor: research.buildingArmor + fortificationArmor, damage: (profile.damage || 0) * research.towerDamage, range: (profile.range || 0) * research.towerRange };
}

function mapConfiguration(options) {
  const requested = typeof options === 'string' ? options : options?.mapSize;
  return MAP_SIZES[requested] || LEGACY_MAPS[requested] || MAP_SIZES[DEFAULT_MAP_SIZE];
}

function startPositions(world) {
  const centerX = world.width / 2, centerY = world.height / 2;
  const radiusX = world.width * (world.longSession ? 0.41 : 0.37), radiusY = world.height * (world.longSession ? 0.45 : 0.35);
  return Array.from({ length: 6 }, (_, slot) => {
    const perimeterAngle = 2.62 + slot / 6 * TAU, x = centerX + Math.cos(perimeterAngle) * radiusX, y = centerY + Math.sin(perimeterAngle) * radiusY;
    return { x, y, angle: Math.atan2(centerY - y, centerX - x) };
  });
}

function teamAssignment(playerFaction, seed, factionIds = Object.keys(FACTIONS)) {
  const candidates = factionIds.filter(factionId => factionId !== playerFaction), random = rng((seed >>> 0) ^ 0x51f15e7d);
  for (let index = candidates.length - 1; index > 0; index--) { const chosen = Math.floor(random() * (index + 1)); [candidates[index], candidates[chosen]] = [candidates[chosen], candidates[index]]; }
  const allyCount = Math.max(0, Math.ceil(factionIds.length / 2) - 1);
  return { allies: candidates.slice(0, allyCount), enemies: candidates.slice(allyCount) };
}

function recordCommand(state, entry) {
  state.commandLog.push({ t: Number(state.elapsed.toFixed(2)), ...entry });
  if (state.commandLog.length > MAX_COMMAND_LOG) state.commandLog.splice(0, state.commandLog.length - MAX_COMMAND_LOG);
}

function emitEvent(state, type, subject, detail = {}) {
  if (!state.events) state.events = [];
  state.events.push({ id: `event-${state.commandSequence++}`, t: Number(state.elapsed.toFixed(3)), type, subjectId: subject?.id || null, x: subject?.x, y: subject?.y, ...detail });
  if (state.events.length > MAX_EVENTS) state.events.splice(0, state.events.length - MAX_EVENTS);
}

function setActivity(state, unit, activity) {
  if (unit.activity !== activity) {
    unit.activity = activity;
    unit.activityStartedAt = Number(state.elapsed.toFixed(3));
  }
  if (unit.job) unit.job.phase = activity;
}

function cloneOrder(order) {
  const copy = { ...order };
  if (order.resume) copy.resume = cloneOrder(order.resume);
  if (order.points) copy.points = order.points.map(point => ({ ...point }));
  return copy;
}

function worksitePosition(site, slot = 0, kind = 'gather') {
  const slots = kind === 'gather' ? GATHERERS_PER_NODE : BUILD_WORK_SLOTS;
  const serial = Number(String(site.id).split('-')[1] || 0);
  const angle = (slot % slots) / slots * TAU + serial * 0.37;
  const radius = (site.radius || 20) + (kind === 'gather' ? 9 : 11);
  return { index: slot, x: site.x + Math.cos(angle) * radius, y: site.y + Math.sin(angle) * radius };
}

function exitWorksite(state, unit, keepAssignment = false) {
  const world = worldOf(state);
  if (unit.insideWorksite && unit.workSlot && Number.isFinite(unit.workSlot.x) && Number.isFinite(unit.workSlot.y)) {
    unit.x = clamp(unit.workSlot.x, 10, world.width - 10);
    unit.y = clamp(unit.workSlot.y, 10, world.height - 10);
    emitEvent(state, 'worksite-exit', unit, { worksiteId: unit.worksiteId, workSlot: unit.workSlot.index });
  }
  unit.insideWorksite = false;
  if (!keepAssignment) { unit.worksiteId = null; unit.workSlot = null; }
}

function exitGarrison(state, unit, emit = true) {
  if (!unit.insideGarrison) return;
  const building = state.buildings.find(item => item.id === unit.garrisonId), serial = Number(String(unit.id).split('-')[1] || 0), angle = serial * 2.399;
  if (building) {
    building.garrisonIds = (building.garrisonIds || []).filter(unitId => unitId !== unit.id);
    unit.x = clamp(building.x + Math.cos(angle) * (building.radius + 20), 12, worldOf(state).width - 12);
    unit.y = clamp(building.y + Math.sin(angle) * (building.radius + 20), 12, worldOf(state).height - 12);
  }
  const previousGarrisonId = unit.garrisonId; unit.insideGarrison = false; unit.garrisonId = null;
  if (emit) emitEvent(state, 'garrison-exit', unit, { faction: unit.faction, buildingId: previousGarrisonId });
}

function nextWorkSlot(state, worksiteId, unitId, kind) {
  const maxSlots = kind === 'gather' ? GATHERERS_PER_NODE : BUILD_WORK_SLOTS;
  const occupied = new Set(state.units.filter(item => item.id !== unitId && alive(item) && item.worksiteId === worksiteId && item.workSlot).map(item => item.workSlot.index));
  for (let slot = 0; slot < maxSlots; slot++) if (!occupied.has(slot)) return slot;
  return [...occupied].length % maxSlots;
}

function attachWorksite(state, unit, order) {
  if (!['gather', 'build', 'repair'].includes(order.kind)) return;
  const site = getEntity(state, order.targetId);
  if (!site) return;
  const occupied = new Set(state.units.filter(item => item.id !== unit.id && alive(item) && item.worksiteId === site.id && item.workSlot).map(item => item.workSlot.index));
  const slot = Number.isInteger(order.slot) && !occupied.has(order.slot) ? order.slot : nextWorkSlot(state, site.id, unit.id, order.kind);
  order.slot = slot;
  unit.worksiteId = site.id;
  unit.workSlot = worksitePosition(site, slot, order.kind);
  unit.insideWorksite = false;
}

function initialActivity(order) {
  if (order.kind === 'idle') return 'idle';
  if (order.kind === 'gather' && order.phase === 'return') return 'returning';
  if (order.kind === 'hold') return 'holding';
  if (order.kind === 'attack') return 'attacking';
  if (order.kind === 'defend') return 'guarding';
  if (order.kind === 'scout' && order.phase === 'survey') return 'scouting';
  return 'moving';
}

function activateUnitOrder(state, unit, order, jobKind = order.kind) {
  exitWorksite(state, unit);
  exitGarrison(state, unit);
  unit.order = cloneOrder(order);
  unit.orderIssuedAt = Number(state.elapsed.toFixed(3));
  unit.job = {
    kind: jobKind === 'move' ? 'travel' : jobKind,
    targetId: unit.order.targetId || null,
    resourceType: unit.order.resourceType || null,
    assignedAt: unit.orderIssuedAt,
  };
  unit.stance = unit.order.kind === 'hold' ? 'hold' : ['patrol', 'defend'].includes(unit.order.kind) ? 'defensive' : 'responsive';
  attachWorksite(state, unit, unit.order);
  setActivity(state, unit, initialActivity(unit.order));
}

function setUnitOrder(state, unit, order, jobKind = order.kind, options = {}) {
  unit.orderQueue ||= [];
  const append = Boolean(options.append || options.shift || options.queue);
  if (append && unit.order?.kind !== 'idle') {
    unit.orderQueue.push({ order: cloneOrder(order), jobKind });
    return;
  }
  if (!append) unit.orderQueue = [];
  activateUnitOrder(state, unit, order, jobKind);
}

function completeUnitOrder(state, unit) {
  unit.orderQueue ||= [];
  const next = unit.orderQueue.shift();
  if (next) activateUnitOrder(state, unit, next.order, next.jobKind);
  else activateUnitOrder(state, unit, { kind: 'idle' }, 'idle');
}

function idleUnit(state, unit) {
  unit.orderQueue = [];
  activateUnitOrder(state, unit, { kind: 'idle' }, 'idle');
}

function addBuilding(state, factionId, type, x, y, complete = true) {
  const profile = effectiveBuildingProfile(state, factionId, type, 0);
  const grandInfluence = worldOf(state).settlementInfluence || 500;
  const building = {
    id: id(state, 'b'), faction: factionId, type, x, y, radius: profile.radius,
    hp: complete ? profile.hp : Math.max(10, profile.hp * 0.05), maxHp: profile.hp,
    progress: complete ? 1 : 0.05, complete, queue: [], cooldown: 0,
    rally: null, activity: complete ? 'ready' : 'foundation', assignedWorkers: 0,
    lastSpawnId: null, lastHitAt: null, lastAttackerId: null,
    armor: profile.armor || 0, fortificationLevel: 0, fortificationName: null, fortified: false,
    garrisonIds: [], garrisonCapacity: type === 'heart' ? 4 : type === 'settlement' ? 3 : type === 'tower' ? 2 : type === 'depot' ? 2 : 0,
    defenseCooldown: 0, shieldUntil: 0, shieldArmor: 0,
    influenceRadius: type === 'heart' ? grandInfluence * 1.12 : type === 'settlement' ? grandInfluence : 0,
    knowledgeRate: type === 'heart' ? 0.018 : type === 'settlement' ? 0.045 : 0,
  };
  state.buildings.push(building);
  return building;
}

function addUnit(state, factionId, role, x, y) {
  const profile = effectiveUnitProfile(state, factionId, role);
  const unit = {
    id: id(state, 'u'), faction: factionId, role, x, y, radius: role === 'worker' ? 10 : role === 'hero' ? 15 : 12,
    hp: profile.hp, maxHp: profile.hp, speed: profile.speed, damage: profile.damage, range: profile.range,
    cooldown: 0, attackCooldown: profile.cooldown, sight: profile.sight, order: { kind: 'idle' }, orderQueue: [],
    carryType: null, carryAmount: 0, capacity: profile.capacity || 0, gatherRate: profile.gatherRate || 0,
    kills: 0, job: { kind: 'idle', targetId: null, resourceType: null, assignedAt: state.elapsed },
    activity: 'idle', activityStartedAt: state.elapsed, orderIssuedAt: state.elapsed,
    worksiteId: null, workSlot: null, insideWorksite: false, stance: 'responsive',
    lastHitAt: null, lastAttackerId: null, armor: profile.armor || 0,
    garrisonId: null, insideGarrison: false, shieldUntil: 0, shieldArmor: 0, weakenedUntil: 0,
  };
  if (role === 'hero') {
    const hero = HEROES[factionId];
    Object.assign(unit, {
      type: 'hero', isHero: true, heroId: hero.id, name: hero.name, title: hero.title,
      level: 1, experience: 0, nextLevelXp: 60,
      ability: { ...hero.ability, remaining: 4, lastUsedAt: null }, inspiredUntil: 0,
    });
  }
  state.units.push(unit);
  return unit;
}

function addNode(state, type, x, y, amount = 800, metadata = null) {
  const node = { id: id(state, 'r'), type, x, y, radius: RESOURCE_NODE_RADII[type] || 32, amount, maxAmount: amount, assignedWorkers: 0, ...(metadata || {}) };
  state.nodes.push(node);
  return node;
}

function addGuardian(state, camp, guardianRole, x, y, level = 1) {
  const profile = GUARDIAN_PROFILES[guardianRole];
  const scale = 1 + (level - 1) * 0.16;
  const guardian = {
    id: id(state, 'g'), type: 'guardian', faction: 'neutral', campId: camp.id,
    guardianRole, role: 'guardian', name: profile.name, level,
    x, y, homeX: x, homeY: y, radius: profile.radius,
    hp: Math.round(profile.hp * scale), maxHp: Math.round(profile.hp * scale),
    speed: profile.speed, damage: profile.damage * scale, range: profile.range,
    cooldown: 0, attackCooldown: profile.cooldown, sight: profile.sight,
    order: { kind: 'guard', targetId: null }, activity: 'guarding', activityStartedAt: state.elapsed,
    kills: 0, lastHitAt: null, lastAttackerId: null, deathHandled: false,
  };
  state.guardians.push(guardian); camp.guardianIds.push(guardian.id);
  return guardian;
}

function addCamp(state, x, y, index, random) {
  const campNames = worldOf(state).theme === 'winter'
    ? ['Rime Prowler Den', 'Frostbound Shrine', 'Ice-Fractured Reliquary']
    : ['Prowler Den', 'Mossbound Shrine', 'Fractured Reliquary'];
  const level = 1 + (index % 3), groupId = `guard-pack-${index + 1}`, camp = {
    id: id(state, 'c'), type: 'neutral-camp', campKind: 'treasure-camp', name: campNames[index % campNames.length],
    x, y, radius: 58, level, guardianIds: [], guardGroupId: groupId,
    guardFormation: level === 1 ? 'watch-pair' : level === 2 ? 'shield-ring' : 'relic-cordon',
    treasure: {
      rations: Math.round((45 + level * 20) * .65), timber: Math.round((40 + level * 18) * .65), stone: Math.round((35 + level * 17) * .65),
      lumen: Math.round((10 + level * 6 + Math.floor(random() * 5)) * .65),
    },
    treasureRemaining: true, cleared: false, clearedAt: null, rewardFaction: null,
    discoveredBy: {}, discoveredByTeams: {}, aggroFaction: null, aggroTeamId: null, rewardTeamId: null, activity: 'guarded',
  };
  state.camps.push(camp);
  const expanded = Boolean(worldOf(state).longSession);
  const roles = expanded
    ? level === 1 ? ['prowler', 'prowler', 'sentinel'] : level === 2 ? ['sentinel', 'prowler', 'channeler', 'prowler'] : ['sentinel', 'channeler', 'prowler', 'prowler', 'sentinel']
    : level === 1 ? ['prowler', 'prowler'] : level === 2 ? ['sentinel', 'prowler', 'channeler'] : ['sentinel', 'channeler', 'prowler', 'prowler'];
  roles.forEach((role, slot) => {
    const angle = slot / roles.length * TAU + index * 0.61, radius = expanded ? 68 + (slot % 2) * 14 : 31 + (slot % 2) * 10;
    const guardian = addGuardian(state, camp, role, x + Math.cos(angle) * radius, y + Math.sin(angle) * radius, level);
    guardian.guardGroupId = groupId; guardian.guardSlot = slot;
  });
  camp.guardianCount = roles.length;
  return camp;
}

function resourceEnclaveCamp(camp) { return camp?.campKind === 'resource-enclave'; }

function addResourceEnclave(state, x, y, index) {
  const level = 3, groupId = `enclave-wardens-${index + 1}`, camp = {
    id: id(state, 'c'), type: 'neutral-camp', campKind: 'resource-enclave', faction: 'neutral', teamId: null, owner: null, originSlot: index, name: `Wardened Plenty ${index + 1}`,
    x, y, radius: 72, level, guardianIds: [], guardGroupId: groupId, guardFormation: 'resource-cordon',
    treasure: {}, treasureRemaining: false, resourceNodeIds: [], cleared: false, clearedAt: null, rewardFaction: null,
    discoveredBy: {}, discoveredByTeams: {}, aggroFaction: null, aggroTeamId: null, rewardTeamId: null, activity: 'resource-guarded',
  };
  state.camps.push(camp);
  const depositEntries = Object.entries(RESOURCE_ENCLAVE_RULES.deposits);
  depositEntries.forEach(([type, baseAmount], slot) => {
    const angle = slot / depositEntries.length * TAU + 0.37 + index * 0.19;
    const node = addNode(
      state, type,
      x + Math.cos(angle) * RESOURCE_ENCLAVE_RULES.nodeRadius,
      y + Math.sin(angle) * RESOURCE_ENCLAVE_RULES.nodeRadius,
      Math.round(baseAmount * (state.world.resourceScale || 1)),
      { enclaveId: camp.id, guarded: true, depositClass: 'resource-enclave', owner: null },
    );
    camp.resourceNodeIds.push(node.id);
  });
  RESOURCE_ENCLAVE_RULES.guardianRoles.forEach((role, slot, roles) => {
    const angle = slot / roles.length * TAU + 0.83 + index * 0.31;
    const guardian = addGuardian(state, camp, role, x + Math.cos(angle) * (74 + slot % 2 * 10), y + Math.sin(angle) * (74 + slot % 2 * 10), level);
    guardian.guardGroupId = groupId; guardian.guardSlot = slot;
  });
  camp.guardianCount = camp.guardianIds.length;
  return camp;
}

export function isResourceNodeHarvestable(state, nodeOrId) {
  const node = typeof nodeOrId === 'string' ? state.nodes?.find(item => item.id === nodeOrId) : nodeOrId;
  if (!node || node.amount <= 0) return false;
  if (!node.enclaveId) return true;
  const enclave = state.camps?.find(camp => camp.id === node.enclaveId && resourceEnclaveCamp(camp));
  return Boolean(enclave?.cleared && enclave.guardianIds.every(guardianId => !alive(state.guardians?.find(guardian => guardian.id === guardianId))));
}

function addFauna(state, species, x, y, herdId, index) {
  const profile = FAUNA_PROFILES[species], fauna = {
    id: id(state, 'f'), type: 'fauna', faction: 'neutral', species, faunaRole: profile.faunaRole,
    name: profile.name, herdId, x, y, homeX: x, homeY: y, radius: profile.radius,
    hp: profile.hp, maxHp: profile.hp, speed: profile.speed, sight: profile.sight,
    damage: profile.damage || 0, range: profile.range || 0, cooldown: 0, attackCooldown: profile.cooldown || 0,
    rationYield: profile.rations, activity: 'grazing', activityStartedAt: state.elapsed,
    waypointAngle: (index * 2.399 + (species === 'mossback-auroch' ? 0.8 : 0)) % TAU,
    grazeUntil: state.elapsed + 2.2 + (index % 4) * .25, fleeUntil: 0, fleeFromX: null, fleeFromY: null,
    order: { kind: 'graze' }, kills: 0, lastHitAt: null, lastAttackerId: null, deathHandled: false,
  };
  state.fauna.push(fauna); return fauna;
}

function createLandmarks(state) {
  const count = worldOf(state).landmarks || (worldOf(state).longSession ? 12 : 6), centerX = state.world.width / 2, centerY = state.world.height / 2;
  const names = ['Whispering Menhir', 'Reedglass Ford', 'Wind-Carved Arch', 'Moss Lantern', 'Old Survey Stone', 'Sunken Waypost'];
  return Array.from({ length: count }, (_, index) => {
    const angle = index / count * TAU + 0.71, ring = worldOf(state).longSession ? [0.14, 0.23, 0.31][index % 3] : index % 2 ? 0.2 : 0.13;
    return {
      id: `landmark-${index + 1}`, type: 'landmark', name: names[index % names.length],
      x: clamp(centerX + Math.cos(angle) * state.world.width * ring, 90, state.world.width - 90),
      y: clamp(centerY + Math.sin(angle) * state.world.height * ring, 90, state.world.height - 90),
      radius: worldOf(state).longSession ? 72 : 44, discoveredBy: {}, discoveredByTeams: {}, activity: 'unexplored',
    };
  });
}

function createRegions(state) {
  if (!worldOf(state).territoryRegions) return [];
  const count = worldOf(state).territoryRegions, columns = Math.max(3, Math.round(Math.sqrt(count))), rows = Math.ceil(count / columns);
  const cellWidth = state.world.width / columns, cellHeight = state.world.height / rows;
  const names = worldOf(state).theme === 'winter'
    ? ['Rime March', 'White Prairie', 'Frostwater', 'Snowcloud Fen', 'Iceglass Vale', 'Pineward', 'Windscar', 'Frozen Mere', 'Long Tundra', 'Hoarcairn']
    : ['Reed March', 'Amber Prairie', 'Stonewater', 'Cloud Fen', 'Lumen Vale', 'Mossward', 'Windscar', 'Glassmere', 'Long Meadow', 'Cairnfield'];
  const regions = [];
  for (let index = 0; index < count; index++) {
    const column = index % columns, row = Math.floor(index / columns);
    regions.push({
      id: `region-${row + 1}-${column + 1}`, type: 'territory-region', name: `${names[index % names.length]} ${index + 1}`,
      x: (column + 0.5) * cellWidth, y: (row + 0.5) * cellHeight,
      radius: Math.min(cellWidth, cellHeight) * 0.46, controllingTeamId: null, controllingFaction: null,
      contested: false, influence: {}, discoveredBy: {}, discoveredByTeams: {}, controlledAt: null,
    });
  }
  return regions;
}

function makeMap(state, random, starts) {
  const scale = state.world.resourceScale || 1;
  starts.forEach((start, originSlot) => {
    const offsets = state.world.longSession ? HOME_RESOURCE_LAYOUT : [
      { type: 'rations', x: -110, y: 15, amount: 720 }, { type: 'timber', x: 105, y: 25, amount: 900 },
      { type: 'stone', x: 0, y: -125, amount: 760 }, { type: 'lumen', x: 145, y: -92, amount: 220 },
    ];
    for (const { type, x: ox, y: oy, amount, longSessionOnly, naturalCluster } of offsets) {
      if (longSessionOnly && !state.world.longSession) continue;
      const c = Math.cos(start.angle), s = Math.sin(start.angle);
      addNode(state, type, start.x + ox * c - oy * s, start.y + ox * s + oy * c, Math.round(amount * scale), state.world.longSession ? { depositClass: 'home', originSlot, naturalCluster } : null);
    }
    if (state.world.longSession) for (const { type, x: ox, y: oy, amount, naturalCluster } of NEARBY_RESOURCE_LAYOUT) {
      const c = Math.cos(start.angle), s = Math.sin(start.angle);
      addNode(state, type, start.x + ox * c - oy * s, start.y + ox * s + oy * c, Math.round(amount * scale), { depositClass: 'settlement-support', originSlot, naturalCluster });
    }
  });
  const center = { x: state.world.width / 2, y: state.world.height / 2 };
  const contestedCount = state.world.contestedNodes || (state.mapSize === 'frontier' ? 8 : state.mapSize === 'dominion' ? 16 : 12);
  for (let index = 0; index < contestedCount; index++) {
    const type = RESOURCE_TYPES[index % RESOURCE_TYPES.length], angle = index / contestedCount * TAU + 0.19;
    const ring = state.world.longSession ? [0.12, 0.21, 0.3][index % 3] : index % 2 ? 0.19 : 0.28;
    const x = center.x + Math.cos(angle) * state.world.width * ring + (random() - 0.5) * 54;
    const y = center.y + Math.sin(angle) * state.world.height * ring + (random() - 0.5) * 44;
    const variation = state.world.longSession ? 0.72 + random() * 0.68 : 1;
    addNode(state, type, x, y, Math.round((type === 'lumen' ? 420 : 1050) * scale * variation));
  }
  state.wells.push(
    { id: id(state, 'w'), type: 'lumen-well', x: center.x, y: center.y + state.world.height * 0.11, radius: 48, owner: null, ownerTeamId: null, challenger: null, challengerTeamId: null, capture: 0 },
    { id: id(state, 'w'), type: 'lumen-well', x: center.x - state.world.width * 0.13, y: center.y - state.world.height * 0.1, radius: 48, owner: null, ownerTeamId: null, challenger: null, challengerTeamId: null, capture: 0 },
    { id: id(state, 'w'), type: 'lumen-well', x: center.x + state.world.width * 0.13, y: center.y - state.world.height * 0.1, radius: 48, owner: null, ownerTeamId: null, challenger: null, challengerTeamId: null, capture: 0 },
  );
  for (let index = 0; index < state.world.camps; index++) {
    // Five clearly separated objective rings distribute neutral guard packs
    // across the enlarged world. Each pack visibly encloses one reward cache;
    // no opening worker begins inside a neutral leash.
    const ringIndex = index % 5, ringSlots = Math.ceil((state.world.camps - ringIndex) / 5), slot = Math.floor(index / 5);
    const angle = slot / ringSlots * TAU + 0.43 + ringIndex * 0.71;
    const ring = state.world.longSession ? [0.10, 0.16, 0.22, 0.28, 0.34][ringIndex] : ringIndex % 2 ? 0.11 : 0.08;
    const jitterX = (random() - .5) * state.world.width * .008, jitterY = (random() - .5) * state.world.height * .008;
    const x = clamp(center.x + Math.cos(angle) * state.world.width * ring + jitterX, 180, state.world.width - 180);
    const y = clamp(center.y + Math.sin(angle) * state.world.height * ring + jitterY, 180, state.world.height - 180);
    addCamp(state, x, y, index, random);
  }
  if (state.world.longSession) {
    const count = state.world.resourceEnclaves || RESOURCE_ENCLAVE_RULES.count;
    for (let index = 0; index < count; index++) {
      // Every opening slot receives one equally distant clockwise frontier
      // enclave, outside its base and clear of the inner treasure-camp rings.
      const start = starts[index % starts.length], travel = Math.min(state.world.width, state.world.height) * 0.22;
      addResourceEnclave(state, start.x - Math.sin(start.angle) * travel, start.y + Math.cos(start.angle) * travel, index);
    }
  }
  const faunaCounts = state.world.reedhorns ? { reedhorn: state.world.reedhorns, 'mossback-auroch': state.world.aurochs }
    : state.mapSize === 'frontier' ? { reedhorn: 10, 'mossback-auroch': 3 }
      : state.mapSize === 'dominion' ? { reedhorn: 24, 'mossback-auroch': 8 } : { reedhorn: 16, 'mossback-auroch': 5 };
  for (const [species, count] of Object.entries(faunaCounts)) {
    const herdSize = species === 'reedhorn' ? 4 : 2;
    for (let index = 0; index < count; index++) {
      const herdIndex = Math.floor(index / herdSize), angle = (herdIndex * 1.73 + (species === 'mossback-auroch' ? 1.1 : 0.2)) % TAU;
      const ring = species === 'mossback-auroch' ? 0.35 : 0.39, slot = index % herdSize;
      const baseX = center.x + Math.cos(angle) * state.world.width * ring;
      const baseY = center.y + Math.sin(angle) * state.world.height * ring;
      const x = clamp(baseX + Math.cos(angle + slot * 1.7) * (18 + slot * 7) + (random() - 0.5) * 14, 45, state.world.width - 45);
      const y = clamp(baseY + Math.sin(angle + slot * 1.7) * (18 + slot * 7) + (random() - 0.5) * 14, 45, state.world.height - 45);
      addFauna(state, species, x, y, `${species}-herd-${herdIndex + 1}`, index);
    }
  }
  state.regions = createRegions(state); state.landmarks = createLandmarks(state);
}

export function createGame(playerFaction = 'hearth', seed = Date.now(), options = {}) {
  if (!FACTIONS[playerFaction]) playerFaction = 'hearth';
  const world = mapConfiguration(options), starts = startPositions(world), assignment = teamAssignment(playerFaction, seed);
  const playerTeamId = 'team-player', enemyTeamId = 'team-enemy';
  const state = {
    schema: 1, seed, nextId: 1, elapsed: 0, paused: false, outcome: null, playerFaction,
    playerTeamId, enemyTeamId, winnerTeamId: null,
    mapSize: world.id, world: { ...world },
    teams: [
      { id: playerTeamId, name: 'Meadow Alliance', memberIds: [playerFaction, ...assignment.allies], human: true, holdSeconds: 0, dominationSeconds: 0, territoryCount: 0, territoryShare: 0, defeated: false, exploredCells: [] },
      { id: enemyTeamId, name: 'Opposing Coalition', memberIds: assignment.enemies, human: false, holdSeconds: 0, dominationSeconds: 0, territoryCount: 0, territoryShare: 0, defeated: false, exploredCells: [] },
    ],
    factions: [], units: [], buildings: [], nodes: [], wells: [], camps: [], guardians: [], fauna: [], regions: [], landmarks: [], influenceZones: [], tradeRoutes: [], expeditions: [], particles: [], events: [], commandLog: [], commandSequence: 1,
    exploration: explorationGrid(world), contactState: { firstContactAt: null, pairs: {} }, strategicClock: world.strategicInterval || 0.5,
    stats: { trained: 0, gathered: 0, lost: 0, defeated: 0, alliesLost: 0, campsCleared: 0, treasure: 0, discoveries: 0, regionsDiscovered: 0, hunted: 0, huntRations: 0, tradeIncome: 0, technologies: 0 }, message: world.longSession ? 'Forage, scout, and establish the first borders of a long campaign.' : 'Coordinate your three-faction alliance and secure the meadow.',
  };
  const order = [playerFaction, ...assignment.allies, ...assignment.enemies];
  order.forEach((factionId, slot) => {
    const teamId = slot < 3 ? playerTeamId : enemyTeamId;
    state.factions.push({
      id: factionId, slot, teamId, ai: slot !== 0, defeated: false,
      stock: world.longSession ? { rations: 110, timber: 90, stone: 70, lumen: 4 } : { rations: 145, timber: 120, stone: 95, lumen: 12 },
      population: 0, capacity: 0, holdSeconds: 0, aiClock: 0.4 + slot * 0.6, waveClock: 30 + slot * 9,
      rebalanceClock: 5 + slot * 1.5, campClock: 18 + slot * 4, huntClock: 13 + slot * 3, fortifyClock: 45 + slot * 5,
      age: 1, ageId: AGES[1].id, ageName: AGES[1].name, townHallLevel: 1, townHallId: AGES[1].id,
      ageResearch: null, upgrades: [], researches: [], research: null, technologies: [], knowledge: 0, knowledgeRate: 0, technologyProgress: null,
      settlementCount: 0, tradeIncome: 0, territoryCount: 0, aggressionPhase: world.longSession ? 'forage' : 'war', expeditionId: null, exploredCells: [], exploredCamps: [], exploredRegions: [],
    });
    const start = starts[slot], heart = addBuilding(state, factionId, 'heart', start.x, start.y, true);
    const sideX = Math.cos(start.angle + Math.PI / 2), sideY = Math.sin(start.angle + Math.PI / 2), backX = -Math.cos(start.angle), backY = -Math.sin(start.angle);
    for (let i = 0; i < 4; i++) addUnit(state, factionId, 'worker', start.x + sideX * (-34 + i * 23) + backX * 52, start.y + sideY * (-34 + i * 23) + backY * 52);
    addUnit(state, factionId, 'melee', start.x + Math.cos(start.angle) * 62, start.y + Math.sin(start.angle) * 62);
    heart.rally = { kind: 'point', x: start.x + Math.cos(start.angle) * 88, y: start.y + Math.sin(start.angle) * 88, targetId: null, setAt: 0 };
  });
  makeMap(state, rng(seed), starts);
  for (const faction of state.factions.filter(item => item.ai)) {
    const heart = state.buildings.find(item => item.faction === faction.id && item.type === 'heart');
    const startNode = nearestNode(state, heart, FACTIONS[faction.id].affinity) || nearestNode(state, heart);
    if (startNode) heart.rally = { kind: 'gather', x: startNode.x, y: startNode.y, targetId: startNode.id, resourceType: startNode.type, setAt: 0 };
  }
  recalculatePopulation(state); updateExploredCells(state);
  return state;
}

export function recalculatePopulation(state) {
  for (const faction of state.factions) {
    faction.population = state.units.filter(unit => unit.faction === faction.id && alive(unit)).reduce((sum, unit) => sum + unitProfile(unit.faction, unit.role, faction.age || 1).pop, 0);
    faction.capacity = state.buildings.filter(building => building.faction === faction.id && alive(building) && building.complete).reduce((sum, building) => sum + (buildingProfile(building.faction, building.type, faction.age || 1).pop || 0), 0);
    faction.settlementCount = state.buildings.filter(building => building.faction === faction.id && building.type === 'settlement' && alive(building) && building.complete).length;
    faction.jobs = { idle: 0, travel: 0, gather: 0, build: 0, repair: 0, defend: 0 };
    for (const worker of state.units.filter(unit => unit.faction === faction.id && unit.role === 'worker' && alive(unit))) {
      const kind = worker.job?.kind || 'idle';
      faction.jobs[kind] = (faction.jobs[kind] || 0) + 1;
    }
  }
}

function refreshAssignments(state) {
  for (const node of state.nodes) node.assignedWorkers = 0;
  for (const building of state.buildings) building.assignedWorkers = 0;
  for (const unit of state.units) {
    if (!alive(unit) || unit.role !== 'worker') continue;
    if (unit.order?.kind === 'gather') {
      const node = state.nodes.find(item => item.id === unit.order.targetId);
      if (node) node.assignedWorkers += 1;
    }
    if (['build', 'repair'].includes(unit.order?.kind)) {
      const building = state.buildings.find(item => item.id === unit.order.targetId);
      if (building) building.assignedWorkers += 1;
    }
  }
}

export function getEntity(state, entityId) {
  return state.units.find(item => item.id === entityId) || state.buildings.find(item => item.id === entityId)
    || state.guardians?.find(item => item.id === entityId) || state.fauna?.find(item => item.id === entityId) || state.camps?.find(item => item.id === entityId)
    || state.landmarks?.find(item => item.id === entityId) || state.regions?.find(item => item.id === entityId)
    || state.nodes.find(item => item.id === entityId) || state.wells.find(item => item.id === entityId) || null;
}

function validUnitIds(state, factionId, unitIds) {
  const requested = new Set(unitIds);
  return state.units.filter(unit => requested.has(unit.id) && unit.faction === factionId && alive(unit));
}

export function issueMove(state, factionId, unitIds, x, y, attackMove = false, options = {}) {
  const units = validUnitIds(state, factionId, unitIds);
  const world = worldOf(state);
  const columns = Math.ceil(Math.sqrt(units.length));
  units.forEach((unit, index) => {
    const offsetX = ((index % columns) - (columns - 1) / 2) * 28;
    const offsetY = (Math.floor(index / columns) - (Math.ceil(units.length / columns) - 1) / 2) * 28;
    setUnitOrder(state, unit, { kind: 'move', x: clamp(x + offsetX, 18, world.width - 18), y: clamp(y + offsetY, 18, world.height - 18), attackMove: Boolean(attackMove) }, attackMove ? 'defend' : 'travel', options);
  });
  recordCommand(state, { faction: factionId, command: attackMove ? 'attack-move' : 'move', count: units.length, unitIds: units.map(item => item.id), x: Math.round(x), y: Math.round(y), queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'destination', { id: null, x, y }, { faction: factionId, command: attackMove ? 'attack-move' : 'move', count: units.length });
  return units.length;
}

export function issueGather(state, factionId, unitIds, nodeId, options = {}) {
  const node = state.nodes.find(item => item.id === nodeId && isResourceNodeHarvestable(state, item));
  if (!node) return 0;
  const workers = validUnitIds(state, factionId, unitIds).filter(unit => unit.role === 'worker');
  const selected = new Set(workers.map(unit => unit.id));
  const occupancy = new Map();
  for (const unit of state.units) {
    if (!alive(unit) || selected.has(unit.id) || unit.order?.kind !== 'gather') continue;
    occupancy.set(unit.order.targetId, (occupancy.get(unit.order.targetId) || 0) + 1);
  }
  for (const unit of workers) {
    // A resource click is an exact instruction. Never fan workers out to
    // same-type deposits the player has not selected (and may not have seen).
    const assigned = node, slot = occupancy.get(node.id) || 0;
    occupancy.set(node.id, slot + 1);
    const mustReturn = unit.carryAmount >= unit.capacity - 0.01 || (unit.carryAmount > 0 && unit.carryType !== node.type);
    const dropoff = closestDropoff(state, unit);
    setUnitOrder(state, unit, {
      kind: 'gather', targetId: node.id, requestedTargetId: node.id, resourceType: node.type,
      phase: mustReturn ? 'return' : 'collect', slot: slot % GATHERERS_PER_NODE, dropoffId: dropoff?.id || null,
    }, 'gather', options);
  }
  recordCommand(state, { faction: factionId, command: 'gather', type: node.type, count: workers.length, unitIds: workers.map(item => item.id), targetId: node.id, queued: Boolean(options.shift || options.queue || options.append) });
  if (workers.length) emitEvent(state, 'assignment', node, { faction: factionId, command: 'gather', count: workers.length, resourceType: node.type });
  return workers.length;
}

export function issueAttack(state, factionId, unitIds, targetId, options = {}) {
  const target = getEntity(state, targetId);
  if (!target || !alive(target) || !target.faction || !areEnemies(state, factionId, target.faction) || !factionCurrentlySees(state, factionId, target)) return 0;
  const units = validUnitIds(state, factionId, unitIds);
  for (const unit of units) setUnitOrder(state, unit, {
    kind: 'attack', targetId, lastKnownX: target.x, lastKnownY: target.y,
    lastKnownAt: Number(state.elapsed.toFixed(3)), targetVisible: true,
  }, unit.role === 'worker' ? 'defend' : 'attack', options);
  recordCommand(state, { faction: factionId, command: 'attack', count: units.length, unitIds: units.map(item => item.id), targetId, queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'assignment', target, { faction: factionId, command: 'attack', count: units.length });
  return units.length;
}

export function issueDefend(state, factionId, unitIds, x, y, targetId = null, radius = DEFEND_RADIUS, options = {}) {
  const units = validUnitIds(state, factionId, unitIds);
  const world = worldOf(state);
  const target = targetId ? getEntity(state, targetId) : null;
  const anchorX = clamp(target?.x ?? x, 18, world.width - 18), anchorY = clamp(target?.y ?? y, 18, world.height - 18);
  for (const unit of units) {
    setUnitOrder(state, unit, { kind: 'defend', x: anchorX, y: anchorY, targetId: target?.id || null, radius: clamp(radius, 70, 260) }, 'defend', options);
  }
  recordCommand(state, { faction: factionId, command: 'defend', count: units.length, unitIds: units.map(item => item.id), x: Math.round(anchorX), y: Math.round(anchorY), targetId: target?.id || null, queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'destination', { id: target?.id || null, x: anchorX, y: anchorY }, { faction: factionId, command: 'defend', count: units.length });
  return units.length;
}

export function issueBuild(state, factionId, unitIds, buildingId, options = {}) {
  const building = state.buildings.find(item => item.id === buildingId && item.faction === factionId && alive(item) && !item.complete);
  if (!building) return 0;
  const workers = validUnitIds(state, factionId, unitIds).filter(unit => unit.role === 'worker');
  for (const worker of workers) setUnitOrder(state, worker, { kind: 'build', targetId: building.id, phase: 'travel' }, 'build', options);
  recordCommand(state, { faction: factionId, command: 'build-assist', count: workers.length, unitIds: workers.map(item => item.id), targetId: building.id, buildingType: building.type, queued: Boolean(options.shift || options.queue || options.append) });
  if (workers.length) emitEvent(state, 'assignment', building, { faction: factionId, command: 'build', count: workers.length });
  return workers.length;
}

export function issueRepair(state, factionId, unitIds, buildingId, options = {}) {
  const building = state.buildings.find(item => item.id === buildingId && item.faction === factionId && alive(item));
  if (!building) return 0;
  if (!building.complete) return issueBuild(state, factionId, unitIds, buildingId, options);
  if (building.hp >= building.maxHp - 0.01) return 0;
  const workers = validUnitIds(state, factionId, unitIds).filter(unit => unit.role === 'worker');
  for (const worker of workers) setUnitOrder(state, worker, { kind: 'repair', targetId: building.id }, 'repair', options);
  recordCommand(state, { faction: factionId, command: 'repair', count: workers.length, unitIds: workers.map(item => item.id), targetId: building.id, buildingType: building.type, queued: Boolean(options.shift || options.queue || options.append) });
  if (workers.length) emitEvent(state, 'assignment', building, { faction: factionId, command: 'repair', count: workers.length });
  return workers.length;
}

export function issueHoldPosition(state, factionId, unitIds, options = {}) {
  const units = validUnitIds(state, factionId, unitIds);
  for (const unit of units) {
    setUnitOrder(state, unit, { kind: 'hold', x: options.x ?? unit.x, y: options.y ?? unit.y, radius: Math.max(40, unit.range + 18) }, 'defend', options);
  }
  recordCommand(state, { faction: factionId, command: 'hold', count: units.length, unitIds: units.map(item => item.id), queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'assignment', units[0], { faction: factionId, command: 'hold', count: units.length });
  return units.length;
}

export function issueHold(state, factionId, unitIds, options = {}) {
  return issueHoldPosition(state, factionId, unitIds, options);
}

export function issuePatrol(state, factionId, unitIds, x, y, targetIdOrOptions = null, maybeOptions = {}) {
  const options = targetIdOrOptions && typeof targetIdOrOptions === 'object' ? targetIdOrOptions : (maybeOptions || {});
  const units = validUnitIds(state, factionId, unitIds);
  const world = worldOf(state);
  const columns = Math.ceil(Math.sqrt(units.length));
  units.forEach((unit, index) => {
    const offsetX = ((index % columns) - (columns - 1) / 2) * 24;
    const offsetY = (Math.floor(index / columns) - (Math.ceil(units.length / columns) - 1) / 2) * 24;
    setUnitOrder(state, unit, {
      kind: 'patrol', points: [{ x: unit.x, y: unit.y }, { x: clamp(x + offsetX, 18, world.width - 18), y: clamp(y + offsetY, 18, world.height - 18) }],
      index: 1, radius: DEFEND_RADIUS,
    }, 'defend', options);
  });
  recordCommand(state, { faction: factionId, command: 'patrol', count: units.length, unitIds: units.map(item => item.id), x: Math.round(x), y: Math.round(y), queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'destination', { id: null, x, y }, { faction: factionId, command: 'patrol', count: units.length });
  return units.length;
}

export function issueScout(state, factionId, unitIds, x, y, options = {}) {
  const units = validUnitIds(state, factionId, unitIds);
  const target = options.targetId ? getEntity(state, options.targetId) : null;
  const world = worldOf(state);
  const destination = {
    x: clamp(target?.x ?? x, 18, world.width - 18),
    y: clamp(target?.y ?? y, 18, world.height - 18),
  };
  units.forEach((unit, index) => {
    let scoutX = destination.x, scoutY = destination.y;
    if (target?.type === 'neutral-camp') {
      const dx = unit.x - target.x, dy = unit.y - target.y, length = Math.max(1, Math.hypot(dx, dy));
      const surveyDistance = Math.max(target.radius + 120, Math.min(285, (unit.sight || 190) + target.radius - 8));
      scoutX = target.x + dx / length * surveyDistance; scoutY = target.y + dy / length * surveyDistance;
    }
    const angle = index / Math.max(1, units.length) * TAU;
    setUnitOrder(state, unit, {
      kind: 'scout', x: clamp(scoutX + Math.cos(angle) * Math.min(18, index * 5), 18, world.width - 18),
      y: clamp(scoutY + Math.sin(angle) * Math.min(18, index * 5), 18, world.height - 18),
      targetId: target?.id || null, phase: 'travel', surveyTime: 1.1,
    }, 'scout', options);
  });
  recordCommand(state, { faction: factionId, command: 'scout', count: units.length, unitIds: units.map(item => item.id), targetId: target?.id || null, x: Math.round(destination.x), y: Math.round(destination.y), queued: Boolean(options.shift || options.queue || options.append) });
  if (units.length) emitEvent(state, 'destination', { id: target?.id || null, ...destination }, { faction: factionId, command: 'scout', count: units.length });
  return units.length;
}

export function issueGarrison(state, factionId, unitIds, buildingId, options = {}) {
  const building = state.buildings.find(item => item.id === buildingId && alive(item) && item.complete && areAllies(state, factionId, item.faction) && (item.garrisonCapacity || 0) > 0);
  if (!building) return 0;
  const units = validUnitIds(state, factionId, unitIds).filter(unit => !unit.insideWorksite);
  const alreadyReserved = state.units.filter(unit => alive(unit) && (unit.insideGarrison && unit.garrisonId === buildingId || unit.order?.kind === 'garrison' && unit.order.targetId === buildingId)).length;
  const available = Math.max(0, building.garrisonCapacity - alreadyReserved), accepted = units.slice().sort((a, b) => a.id.localeCompare(b.id)).slice(0, available);
  for (const unit of accepted) setUnitOrder(state, unit, { kind: 'garrison', targetId: buildingId }, 'defend', options);
  recordCommand(state, { faction: factionId, command: 'garrison', buildingId, count: accepted.length, unitIds: accepted.map(unit => unit.id), queued: Boolean(options.shift || options.queue || options.append) });
  if (accepted.length) emitEvent(state, 'assignment', building, { faction: factionId, command: 'garrison', count: accepted.length });
  return accepted.length;
}

export function ungarrisonUnits(state, factionId, unitIds) {
  const units = validUnitIds(state, factionId, unitIds).filter(unit => unit.insideGarrison);
  for (const unit of units) { exitGarrison(state, unit); idleUnit(state, unit); }
  if (units.length) recordCommand(state, { faction: factionId, command: 'ungarrison', count: units.length, unitIds: units.map(unit => unit.id) });
  return units.length;
}

export function setRallyPoint(state, factionId, buildingIds, x, y, targetId = null, roleHint = null) {
  const requested = new Set(Array.isArray(buildingIds) ? buildingIds : [buildingIds]);
  const buildings = state.buildings.filter(item => requested.has(item.id) && item.faction === factionId && alive(item) && item.complete && ['heart', 'barracks'].includes(item.type));
  const target = targetId ? getEntity(state, targetId) : null;
  const world = worldOf(state);
  let kind = roleHint === 'defend' ? 'defend' : 'point';
  if (target && state.nodes.includes(target) && isResourceNodeHarvestable(state, target)) kind = 'gather';
  else if (target?.faction && areEnemies(state, factionId, target.faction)) kind = 'attack';
  else if (target && state.buildings.includes(target) && target.faction === factionId && !target.complete) kind = 'build';
  else if (target && state.buildings.includes(target) && target.faction === factionId && target.hp < target.maxHp - 0.01) kind = 'repair';
  else if (target && (areAllies(state, factionId, target.faction) || state.wells.includes(target))) kind = 'defend';
  const rally = {
    kind, x: clamp(target?.x ?? x, 18, world.width - 18), y: clamp(target?.y ?? y, 18, world.height - 18),
    targetId: target?.id || null, resourceType: kind === 'gather' ? target.type : null, setAt: Number(state.elapsed.toFixed(3)),
  };
  for (const building of buildings) building.rally = { ...rally };
  recordCommand(state, { faction: factionId, command: 'rally', count: buildings.length, rallyKind: kind, targetId: rally.targetId, x: Math.round(rally.x), y: Math.round(rally.y) });
  if (buildings.length) emitEvent(state, 'rally', { id: null, x: rally.x, y: rally.y }, { faction: factionId, count: buildings.length, rallyKind: kind, targetId: rally.targetId });
  return buildings.length;
}

function agePrerequisites(state, factionId, targetAge) {
  if (targetAge === 2) return [{ type: 'depot', label: FACTIONS[factionId].depot }];
  if (targetAge === 3) return [
    { type: 'barracks', label: FACTIONS[factionId].barracks },
    { type: 'tower', label: FACTIONS[factionId].tower },
    ...(worldOf(state).longSession ? [{ type: 'settlement', label: FACTIONS[factionId].settlement }] : []),
  ];
  if (targetAge === 4) return [
    { type: 'settlement', label: FACTIONS[factionId].settlement },
    { type: 'tower', label: FACTIONS[factionId].tower },
    { researchId: 'frontier-charters', label: RESEARCH['frontier-charters'].name },
    { researchId: 'field-armor', label: RESEARCH['field-armor'].name },
  ];
  if (targetAge === 5) return [
    { type: 'settlement', count: 2, label: `two ${FACTIONS[factionId].settlement} settlements` },
    { researchId: 'border-citadels', label: RESEARCH['border-citadels'].name },
    { researchId: 'lumen-grid', label: RESEARCH['lumen-grid'].name },
    { technologyId: 'far-banner-networks', label: TECHNOLOGIES['far-banner-networks'].name },
  ];
  return [];
}

export function canAdvanceAge(state, factionId) {
  const faction = factionOf(state, factionId);
  if (!faction || faction.defeated) return { ok: false, targetAge: null, reason: 'invalid-faction', cost: null, prerequisites: [] };
  const targetAge = (faction.age || 1) + 1;
  if (targetAge > 5) return { ok: false, targetAge: null, reason: 'maximum-age', cost: null, prerequisites: [] };
  const age = AGES[targetAge], prerequisites = agePrerequisites(state, factionId, targetAge);
  if (faction.ageResearch) return { ok: false, targetAge, reason: 'research-in-progress', cost: age.cost, prerequisites };
  const missing = prerequisites.filter(requirement => {
    if (requirement.researchId) return !(faction.researches || []).includes(requirement.researchId);
    if (requirement.technologyId) return !(faction.technologies || []).includes(requirement.technologyId);
    const count = state.buildings.filter(building => building.faction === factionId && building.type === requirement.type && alive(building) && building.complete).length;
    return count < (requirement.count || 1);
  });
  if (missing.length) return { ok: false, targetAge, reason: 'missing-prerequisite', cost: age.cost, prerequisites, missing };
  if (!canAfford(faction.stock, age.cost)) return { ok: false, targetAge, reason: 'insufficient-resources', cost: age.cost, prerequisites };
  return { ok: true, targetAge, reason: null, cost: age.cost, prerequisites };
}

export function beginAgeAdvance(state, factionId) {
  const check = canAdvanceAge(state, factionId);
  if (!check.ok) return false;
  const faction = factionOf(state, factionId), age = AGES[check.targetAge];
  spend(faction.stock, age.cost);
  faction.ageResearch = {
    targetAge: check.targetAge, ageId: age.id, ageName: age.name,
    remaining: age.researchTime, total: age.researchTime, startedAt: Number(state.elapsed.toFixed(3)),
  };
  const heart = state.buildings.find(building => building.faction === factionId && building.type === 'heart' && alive(building));
  emitEvent(state, 'age-up-started', heart, { faction: factionId, targetAge: check.targetAge, ageId: age.id });
  recordCommand(state, { faction: factionId, command: 'age-up', targetAge: check.targetAge, ageId: age.id });
  return true;
}

function refreshFactionStats(state, faction) {
  for (const unit of state.units.filter(item => item.faction === faction.id)) {
    const ratio = unit.maxHp > 0 ? unit.hp / unit.maxHp : 0, profile = effectiveUnitProfile(state, faction.id, unit.role), heroScale = unit.role === 'hero' ? 1.08 ** Math.max(0, (unit.level || 1) - 1) : 1;
    unit.maxHp = Math.round(profile.hp * heroScale); unit.hp = Math.max(0, Math.min(unit.maxHp, unit.maxHp * ratio));
    unit.speed = profile.speed; unit.damage = profile.damage * heroScale; unit.range = profile.range;
    unit.attackCooldown = profile.cooldown; unit.sight = profile.sight; unit.armor = profile.armor || 0;
    if (unit.role === 'worker') unit.gatherRate = profile.gatherRate;
  }
  for (const building of state.buildings.filter(item => item.faction === faction.id)) {
    const ratio = building.maxHp > 0 ? building.hp / building.maxHp : 0, profile = effectiveBuildingProfile(state, faction.id, building.type, building.fortificationLevel || 0);
    building.maxHp = profile.hp; building.hp = Math.max(0, Math.min(building.maxHp, building.maxHp * ratio)); building.armor = profile.armor || 0;
  }
}

function completeAgeAdvance(state, faction) {
  const targetAge = faction.ageResearch?.targetAge;
  if (!AGES[targetAge]) { faction.ageResearch = null; return; }
  faction.age = targetAge; faction.ageId = AGES[targetAge].id; faction.ageName = AGES[targetAge].name;
  const upgrade = FACTIONS[faction.id].ageBonuses?.[targetAge]?.name || AGES[targetAge].name;
  if (!faction.upgrades.includes(upgrade)) faction.upgrades.push(upgrade);
  faction.townHallLevel = targetAge; faction.townHallId = AGES[targetAge].id;
  refreshFactionStats(state, faction);
  faction.ageResearch = null;
  const heart = state.buildings.find(building => building.faction === faction.id && building.type === 'heart' && alive(building));
  emitEvent(state, 'age-up', heart, { faction: faction.id, age: targetAge, ageId: AGES[targetAge].id, upgrade });
  recalculatePopulation(state);
}

function updateAgeResearch(state, dt) {
  for (const faction of state.factions) {
    if (!faction.ageResearch || faction.defeated) continue;
    faction.ageResearch.remaining = Math.max(0, faction.ageResearch.remaining - dt);
    if (faction.ageResearch.remaining <= 0) completeAgeAdvance(state, faction);
  }
}

export function canResearchUpgrade(state, factionId, researchId, buildingId = null) {
  const faction = factionOf(state, factionId), research = RESEARCH[researchId];
  if (!faction || faction.defeated || !research) return { ok: false, researchId, reason: 'invalid-research', cost: research?.cost || null, sourceId: null };
  if ((faction.researches || []).includes(researchId)) return { ok: false, researchId, reason: 'already-complete', cost: research.cost, sourceId: null };
  if (faction.research) return { ok: false, researchId, reason: 'research-in-progress', cost: research.cost, sourceId: faction.research.sourceId || null };
  if ((faction.age || 1) < research.age) return { ok: false, researchId, reason: 'age-locked', cost: research.cost, sourceId: null };
  const source = state.buildings.filter(building => building.faction === factionId && building.type === research.source && alive(building) && building.complete && !(building.queue || []).length && !building.researching && (!buildingId || building.id === buildingId)).sort((a, b) => a.id.localeCompare(b.id))[0];
  if (!source) return { ok: false, researchId, reason: 'missing-source', cost: research.cost, sourceId: null, sourceType: research.source };
  if (!canAfford(faction.stock, research.cost)) return { ok: false, researchId, reason: 'insufficient-resources', cost: research.cost, sourceId: source.id, sourceType: research.source };
  return { ok: true, researchId, reason: null, cost: research.cost, sourceId: source.id, sourceType: research.source, researchTime: research.researchTime };
}

export function availableResearch(state, factionId) {
  return Object.keys(RESEARCH).map(researchId => ({ ...RESEARCH[researchId], ...canResearchUpgrade(state, factionId, researchId) }));
}

export function beginResearchUpgrade(state, factionId, researchId, buildingId = null) {
  const check = canResearchUpgrade(state, factionId, researchId, buildingId);
  if (!check.ok) return false;
  const faction = factionOf(state, factionId), research = RESEARCH[researchId], source = getEntity(state, check.sourceId);
  spend(faction.stock, research.cost);
  faction.research = { id: researchId, name: research.name, sourceId: source.id, sourceType: research.source, remaining: research.researchTime, total: research.researchTime, startedAt: Number(state.elapsed.toFixed(3)) };
  source.researching = researchId; source.activity = 'researching';
  emitEvent(state, 'research-started', source, { faction: factionId, researchId, researchName: research.name });
  recordCommand(state, { faction: factionId, command: 'research', researchId, buildingId: source.id });
  return true;
}

function updateResearch(state, dt) {
  for (const faction of state.factions) {
    if (!faction.research || faction.defeated) continue;
    faction.research.remaining = Math.max(0, faction.research.remaining - dt);
    if (faction.research.remaining > 0) continue;
    const researchId = faction.research.id, research = RESEARCH[researchId], source = getEntity(state, faction.research.sourceId);
    if (!faction.researches.includes(researchId)) faction.researches.push(researchId);
    if (!faction.upgrades.includes(research.name)) faction.upgrades.push(research.name);
    if (source) { source.researching = null; source.activity = source.queue?.length ? 'training' : 'ready'; }
    faction.research = null; refreshFactionStats(state, faction);
    emitEvent(state, 'research-complete', source, { faction: faction.id, researchId, researchName: research.name, effects: research.effects });
  }
}

export function canFortifyBuilding(state, factionId, buildingId) {
  const faction = factionOf(state, factionId), building = state.buildings.find(item => item.id === buildingId && item.faction === factionId && alive(item) && item.complete);
  if (!faction || !building) return { ok: false, reason: 'invalid-building', nextLevel: null, cost: null };
  const nextLevel = (building.fortificationLevel || 0) + 1, level = FORTIFICATION_LEVELS[nextLevel];
  if (!level) return { ok: false, reason: 'maximum-fortification', nextLevel: null, cost: null };
  if ((faction.age || 1) < level.age) return { ok: false, reason: 'age-locked', nextLevel, cost: level.cost };
  if (!canAfford(faction.stock, level.cost)) return { ok: false, reason: 'insufficient-resources', nextLevel, cost: level.cost };
  return { ok: true, reason: null, nextLevel, cost: level.cost, name: level.name };
}

export function fortifyBuilding(state, factionId, buildingId) {
  const check = canFortifyBuilding(state, factionId, buildingId);
  if (!check.ok) return false;
  const faction = factionOf(state, factionId), building = getEntity(state, buildingId), ratio = building.hp / building.maxHp, level = FORTIFICATION_LEVELS[check.nextLevel];
  spend(faction.stock, level.cost); building.fortificationLevel = check.nextLevel; building.fortificationName = level.name; building.fortified = true;
  const profile = effectiveBuildingProfile(state, factionId, building.type, building.fortificationLevel);
  building.maxHp = profile.hp; building.hp = Math.max(1, building.maxHp * ratio); building.armor = profile.armor;
  emitEvent(state, 'fortification-upgraded', building, { faction: factionId, buildingType: building.type, fortificationLevel: building.fortificationLevel, fortificationName: building.fortificationName, armor: building.armor });
  recordCommand(state, { faction: factionId, command: 'fortify', buildingId, fortificationLevel: building.fortificationLevel });
  return true;
}

export function canEstablishTradeRoute(state, factionId, sourceId, targetId) {
  const faction = factionOf(state, factionId), source = state.buildings.find(building => building.id === sourceId), target = state.buildings.find(building => building.id === targetId);
  if (!faction || faction.defeated || !source || !target || !alive(source) || !alive(target) || !source.complete || !target.complete) return { ok: false, reason: 'invalid-endpoint', cost: TRADE_RULES.cost };
  if (source.faction !== factionId || source.type !== 'settlement' || !['heart', 'settlement'].includes(target.type) || !areAllies(state, factionId, target.faction)) return { ok: false, reason: 'invalid-endpoint', cost: TRADE_RULES.cost };
  const routeDistance = distance(source, target);
  if (routeDistance < TRADE_RULES.minimumDistance) return { ok: false, reason: 'route-too-short', cost: TRADE_RULES.cost, distance: routeDistance };
  const duplicate = (state.tradeRoutes || []).some(route => route.active !== false && route.faction === factionId && route.sourceId === sourceId && route.targetId === targetId);
  if (duplicate) return { ok: false, reason: 'route-exists', cost: TRADE_RULES.cost, distance: routeDistance };
  const capacity = 1 + (faction.settlementCount || 0) * 2, active = (state.tradeRoutes || []).filter(route => route.faction === factionId && route.active !== false).length;
  if (active >= capacity) return { ok: false, reason: 'trade-capacity', cost: TRADE_RULES.cost, distance: routeDistance, capacity };
  if (!canAfford(faction.stock, TRADE_RULES.cost)) return { ok: false, reason: 'insufficient-resources', cost: TRADE_RULES.cost, distance: routeDistance, capacity };
  return { ok: true, reason: null, cost: TRADE_RULES.cost, distance: routeDistance, capacity, partnerFaction: target.faction };
}

export function establishTradeRoute(state, factionId, sourceId, targetId) {
  const check = canEstablishTradeRoute(state, factionId, sourceId, targetId);
  if (!check.ok) return null;
  const faction = factionOf(state, factionId); spend(faction.stock, TRADE_RULES.cost);
  const route = {
    id: id(state, 'trade'), type: 'trade-route', faction: factionId, teamId: faction.teamId,
    sourceId, targetId, partnerFaction: check.partnerFaction, distance: Number(check.distance.toFixed(2)),
    cycleSeconds: TRADE_RULES.cycleSeconds, progress: 0, deliveries: 0, totalIncome: 0,
    active: true, establishedAt: Number(state.elapsed.toFixed(3)), lastDeliveryAt: null, income: null,
  };
  state.tradeRoutes ||= []; state.tradeRoutes.push(route);
  recordCommand(state, { faction: factionId, command: 'trade-route', routeId: route.id, sourceId, targetId, partnerFaction: route.partnerFaction });
  emitEvent(state, 'trade-route-established', state.buildings.find(building => building.id === sourceId), { faction: factionId, teamId: faction.teamId, routeId: route.id, sourceId, targetId, partnerFaction: route.partnerFaction, distance: route.distance });
  return route;
}

function expeditionTargetSet(state, factionId, hero, definition) {
  const faction = factionOf(state, factionId), teamId = faction.teamId;
  const landmark = (state.landmarks || []).slice().sort((a, b) => {
    const unknownA = a.discoveredBy?.[factionId] === undefined ? 0 : 1, unknownB = b.discoveredBy?.[factionId] === undefined ? 0 : 1;
    return unknownA - unknownB || distance(hero, a) - distance(hero, b) || a.id.localeCompare(b.id);
  })[0];
  const camp = (state.camps || []).filter(item => !item.cleared && !resourceEnclaveCamp(item)).sort((a, b) => distance(landmark || hero, a) - distance(landmark || hero, b) || a.id.localeCompare(b.id))[0];
  const viable = animals => animals.filter(animal => alive(animal) && (state.fauna || []).filter(member => member.herdId === animal.herdId && (alive(member) || member.huntedByTeamId === teamId)).length >= definition.huntCount);
  const preferred = viable((state.fauna || []).filter(animal => animal.species === definition.preferredFauna));
  const candidates = preferred.length ? preferred : viable(state.fauna || []);
  const animal = candidates.slice().sort((a, b) => distance(camp || landmark || hero, a) - distance(camp || landmark || hero, b) || a.id.localeCompare(b.id))[0];
  const home = state.buildings.filter(building => building.faction === factionId && alive(building) && building.complete && ['heart', 'settlement'].includes(building.type))
    .sort((a, b) => (a.type === 'heart' ? -1 : 0) - (b.type === 'heart' ? -1 : 0) || a.id.localeCompare(b.id))[0];
  if (!landmark || !camp || !animal || !home) return null;
  const shared = { campId: camp.id, herdId: animal.herdId };
  return definition.stages.map(kind => {
    if (kind === 'scout-landmark') return { kind, label: `Survey ${landmark.name}`, targetId: landmark.id, complete: false };
    if (kind === 'clear-camp') return { kind, label: `Clear ${camp.name}`, targetId: camp.id, ...shared, complete: false };
    if (kind === 'hunt-herd') return { kind, label: `Provision from ${animal.name}`, targetId: animal.id, herdId: animal.herdId, required: definition.huntCount, progress: 0, complete: false };
    if (kind === 'recover-treasure') return { kind, label: `Recover ${camp.name} cache`, targetId: camp.id, ...shared, complete: false };
    return { kind: 'return-home', label: `Return to ${FACTIONS[factionId].heart}`, targetId: home.id, complete: false };
  });
}

export function availableExpeditions(state, factionId) {
  const faction = factionOf(state, factionId), hero = state.units.find(unit => unit.faction === factionId && unit.role === 'hero' && alive(unit));
  const active = (state.expeditions || []).find(expedition => expedition.faction === factionId && expedition.status === 'active');
  return Object.values(EXPEDITION_QUESTS).map(definition => ({
    ...definition, available: Boolean(faction && hero && !active && expeditionTargetSet(state, factionId, hero, definition)),
    reason: !faction || faction.defeated ? 'invalid-faction' : !hero ? 'hero-required' : active ? 'expedition-active' : expeditionTargetSet(state, factionId, hero, definition) ? null : 'targets-unavailable',
  }));
}

export function beginExpedition(state, factionId, heroUnitId, questId = 'lantern-road') {
  const faction = factionOf(state, factionId), definition = EXPEDITION_QUESTS[questId], hero = state.units.find(unit => unit.id === heroUnitId && unit.faction === factionId && unit.role === 'hero' && alive(unit));
  if (!faction || faction.defeated || !definition || !hero || (state.expeditions || []).some(expedition => expedition.faction === factionId && expedition.status === 'active')) return null;
  const stages = expeditionTargetSet(state, factionId, hero, definition); if (!stages) return null;
  const expedition = {
    id: id(state, 'expedition'), type: 'hero-expedition', questId, name: definition.name, description: definition.description,
    faction: factionId, teamId: faction.teamId, heroUnitId: hero.id, status: 'active', stageIndex: 0,
    stages, currentStage: stages[0], startedAt: Number(state.elapsed.toFixed(3)), completedAt: null, reward: { ...definition.reward },
  };
  state.expeditions ||= []; state.expeditions.push(expedition); faction.expeditionId = expedition.id;
  hero.expeditionId = expedition.id; hero.expeditionStage = expedition.currentStage.kind; hero.expeditionStatus = 'active';
  emitEvent(state, 'expedition-started', hero, { faction: factionId, teamId: faction.teamId, expeditionId: expedition.id, questId, questName: definition.name, stageKind: expedition.currentStage.kind, targetId: expedition.currentStage.targetId });
  recordCommand(state, { faction: factionId, command: 'expedition-begin', expeditionId: expedition.id, questId, heroUnitId: hero.id });
  advanceExpedition(state, factionId, expedition.id);
  return expedition;
}

export function advanceExpedition(state, factionId, expeditionId) {
  const expedition = (state.expeditions || []).find(item => item.id === expeditionId && item.faction === factionId && item.status === 'active');
  const hero = expedition && state.units.find(unit => unit.id === expedition.heroUnitId && alive(unit));
  const stage = expedition?.currentStage;
  if (!hero || !stage) return false;
  let issued = false;
  if (stage.kind === 'scout-landmark') {
    const landmark = getEntity(state, stage.targetId); issued = Boolean(landmark && issueScout(state, factionId, [hero.id], landmark.x, landmark.y, { targetId: landmark.id }));
  } else if (stage.kind === 'clear-camp') {
    const camp = getEntity(state, stage.targetId), guardian = camp?.guardianIds?.map(guardianId => getEntity(state, guardianId)).find(alive);
    issued = guardian && factionCurrentlySees(state, factionId, guardian)
      ? Boolean(issueAttack(state, factionId, [hero.id], guardian.id))
      : Boolean(camp && issueScout(state, factionId, [hero.id], camp.x, camp.y, { targetId: camp.id }));
  } else if (stage.kind === 'hunt-herd') {
    const animals = (state.fauna || []).filter(item => alive(item) && item.herdId === stage.herdId).sort((a, b) => distance(hero, a) - distance(hero, b) || a.id.localeCompare(b.id));
    const animal = animals.find(item => factionCurrentlySees(state, factionId, item));
    const searchTarget = animal || animals[0] || getEntity(state, stage.targetId);
    issued = animal
      ? Boolean(issueAttack(state, factionId, [hero.id], animal.id))
      : Boolean(searchTarget && issueScout(state, factionId, [hero.id], searchTarget.x, searchTarget.y, { targetId: searchTarget.id }));
  } else {
    const target = getEntity(state, stage.targetId); issued = Boolean(target && issueMove(state, factionId, [hero.id], target.x, target.y));
  }
  if (issued) recordCommand(state, { faction: factionId, command: 'expedition-advance', expeditionId, stageKind: stage.kind, targetId: stage.targetId, heroUnitId: hero.id });
  return issued;
}

export function cancelExpedition(state, factionId, expeditionId) {
  const expedition = (state.expeditions || []).find(item => item.id === expeditionId && item.faction === factionId && item.status === 'active');
  if (!expedition) return false;
  expedition.status = 'cancelled'; expedition.completedAt = Number(state.elapsed.toFixed(3));
  const faction = factionOf(state, factionId), hero = state.units.find(unit => unit.id === expedition.heroUnitId);
  if (faction?.expeditionId === expedition.id) faction.expeditionId = null;
  if (hero) { hero.expeditionId = null; hero.expeditionStage = null; hero.expeditionStatus = 'cancelled'; }
  emitEvent(state, 'expedition-cancelled', hero, { faction: factionId, teamId: expedition.teamId, expeditionId });
  recordCommand(state, { faction: factionId, command: 'expedition-cancel', expeditionId });
  return true;
}

function queuedPopulation(state, factionId) {
  return state.buildings
    .filter(building => building.faction === factionId && alive(building))
    .flatMap(building => building.queue || [])
    .reduce((sum, item) => sum + (effectiveUnitProfile(state, factionId, item.role)?.pop || 0), 0);
}

export function queueUnit(state, factionId, buildingId, role) {
  const faction = factionOf(state, factionId), building = state.buildings.find(item => item.id === buildingId && item.faction === factionId && alive(item) && item.complete);
  if (!faction || !building || !['worker', 'melee', 'ranged'].includes(role)) return false;
  if (building.researching) return false;
  if (unlockAge('unit', role) > (faction.age || 1)) return false;
  if (role === 'worker' && building.type !== 'heart') return false;
  if (role !== 'worker' && building.type !== 'barracks') return false;
  const profile = effectiveUnitProfile(state, factionId, role), cost = FACTIONS[factionId].costs[role];
  const reservedPopulation = queuedPopulation(state, factionId);
  if (building.queue.length >= PRODUCTION_RULES.maxPendingPerStructure || faction.population + reservedPopulation + profile.pop > faction.capacity || !canAfford(faction.stock, cost)) return false;
  spend(faction.stock, cost);
  building.queue.push({ id: id(state, 'q'), role, remaining: profile.trainTime, total: profile.trainTime, queuedAt: Number(state.elapsed.toFixed(3)) });
  building.activity = 'training';
  recordCommand(state, { faction: factionId, command: 'train', buildingId: building.id, role, queueLength: building.queue.length });
  return true;
}

export function queueHero(state, factionId, buildingId) {
  const faction = factionOf(state, factionId), hero = HEROES[factionId];
  const building = state.buildings.find(item => item.id === buildingId && item.faction === factionId && alive(item) && item.complete && item.type === 'barracks');
  if (!faction || !hero || !building || building.researching || (faction.age || 1) < hero.unlockAge) return false;
  const exists = state.units.some(unit => unit.faction === factionId && unit.role === 'hero' && alive(unit));
  const queued = state.buildings.some(item => item.faction === factionId && (item.queue || []).some(entry => entry.role === 'hero'));
  const profile = effectiveUnitProfile(state, factionId, 'hero'), reservedPopulation = queuedPopulation(state, factionId);
  if (exists || queued || building.queue.length >= PRODUCTION_RULES.maxPendingPerStructure || faction.population + reservedPopulation + profile.pop > faction.capacity || !canAfford(faction.stock, hero.cost)) return false;
  spend(faction.stock, hero.cost);
  building.queue.push({ id: id(state, 'q'), role: 'hero', heroId: hero.id, remaining: profile.trainTime, total: profile.trainTime, queuedAt: Number(state.elapsed.toFixed(3)) });
  building.activity = 'training';
  recordCommand(state, { faction: factionId, command: 'recruit-hero', buildingId: building.id, heroId: hero.id, role: 'hero', queueLength: building.queue.length });
  return true;
}

function clearPlacement(state, x, y, radius) {
  const world = worldOf(state);
  if (x < 70 || y < 70 || x > world.width - 70 || y > world.height - 70) return false;
  if (state.buildings.some(item => alive(item) && Math.hypot(item.x - x, item.y - y) < item.radius + radius + 20)) return false;
  if (state.nodes.some(item => item.amount > 0 && Math.hypot(item.x - x, item.y - y) < item.radius + radius + 18)) return false;
  if (state.wells.some(item => Math.hypot(item.x - x, item.y - y) < item.radius + radius + 30)) return false;
  if ((state.camps || []).some(item => !item.cleared && Math.hypot(item.x - x, item.y - y) < item.radius + radius + 35)) return false;
  if ((state.guardians || []).some(item => alive(item) && Math.hypot(item.x - x, item.y - y) < item.radius + radius + 18)) return false;
  return !(state.fauna || []).some(item => alive(item) && Math.hypot(item.x - x, item.y - y) < item.radius + radius + 12);
}

export function canPlaceBuilding(state, factionId, type, x, y) {
  const faction = factionOf(state, factionId);
  if (!faction || faction.defeated || !BUILDING_COSTS[type]) return false;
  if (unlockAge('building', type) > (faction.age || 1)) return false;
  if (type === 'settlement') {
    const expansion = worldOf(state).settlementExpansion || 720;
    const sources = state.buildings.filter(building => alive(building) && building.complete && ['heart', 'settlement'].includes(building.type) && areAllies(state, factionId, building.faction));
    if (!sources.some(source => distance(source, { x, y }) <= expansion)) return false;
  }
  return canAfford(faction.stock, BUILDING_COSTS[type]) && clearPlacement(state, x, y, effectiveBuildingProfile(state, factionId, type).radius);
}

export function placeBuilding(state, factionId, type, x, y, workerIds = []) {
  if (!canPlaceBuilding(state, factionId, type, x, y)) return null;
  const faction = factionOf(state, factionId); spend(faction.stock, BUILDING_COSTS[type]);
  const building = addBuilding(state, factionId, type, x, y, false);
  const workers = validUnitIds(state, factionId, workerIds).filter(unit => unit.role === 'worker');
  for (const worker of workers) setUnitOrder(state, worker, { kind: 'build', targetId: building.id, phase: 'travel' }, 'build');
  recordCommand(state, { faction: factionId, command: 'build', type, x: Math.round(x), y: Math.round(y), targetId: building.id, count: workers.length });
  emitEvent(state, 'foundation', building, { faction: factionId, buildingType: type, count: workers.length });
  return building;
}

function stepToward(state, unit, target, dt, stop = 0) {
  const dx = target.x - unit.x, dy = target.y - unit.y, d = Math.hypot(dx, dy);
  if (d <= stop + 0.5) return true;
  const amount = Math.min(d - stop, unit.speed * dt);
  unit.facing = Math.atan2(dy, dx);
  unit.x += dx / d * amount; unit.y += dy / d * amount;
  const world = worldOf(state);
  unit.x = clamp(unit.x, 10, world.width - 10); unit.y = clamp(unit.y, 10, world.height - 10);
  return d - amount <= stop + 0.5;
}

function closestDropoff(state, unit, preferredId = null) {
  const valid = state.buildings.filter(item => item.faction === unit.faction && alive(item) && item.complete && ['heart', 'depot', 'settlement'].includes(item.type));
  const preferred = valid.find(item => item.id === preferredId);
  return preferred || valid.sort((a, b) => distance(unit, a) - distance(unit, b) || a.id.localeCompare(b.id))[0];
}

function gatherPosition(node, slot = 0) {
  const angle = (slot % GATHERERS_PER_NODE) / GATHERERS_PER_NODE * TAU + Number(node.id.split('-')[1] || 0) * 0.37;
  const radius = node.radius + 9;
  return { x: node.x + Math.cos(angle) * radius, y: node.y + Math.sin(angle) * radius };
}

function workerAssistMultiplier(state, unit, targetId, orderKind) {
  const assigned = state.units.filter(item => alive(item) && item.role === 'worker' && item.order?.kind === orderKind && item.order.targetId === targetId)
    .sort((a, b) => a.id.localeCompare(b.id));
  const index = assigned.findIndex(item => item.id === unit.id);
  const age = factionOf(state, unit.faction)?.age || 1;
  return index <= 0 ? 1 : unitProfile(unit.faction, 'worker', age).buildAssist;
}

function hostileEntities(state, factionId) {
  return [...state.units, ...state.buildings, ...(state.guardians || []), ...(state.fauna || [])]
    .filter(target => alive(target) && areEnemies(state, factionId, target.faction) && !factionOf(state, target.faction)?.defeated && !target.insideGarrison);
}

function defensiveHostiles(state, factionId) {
  return hostileEntities(state, factionId).filter(target => {
    if (target.faction !== 'neutral') return true;
    const provokingUnit = target.lastAttackerId ? getEntity(state, target.lastAttackerId) : null;
    const activeTarget = target.order?.targetId ? getEntity(state, target.order.targetId) : null;
    const camp = target.campId ? state.camps?.find(item => item.id === target.campId) : null;
    const teamId = factionOf(state, factionId)?.teamId;
    const provokedByTeam = Boolean(provokingUnit && (provokingUnit.faction === factionId || areAllies(state, factionId, provokingUnit.faction)));
    const attackingTeam = Boolean(activeTarget && (activeTarget.faction === factionId || areAllies(state, factionId, activeTarget.faction)));
    // Fleeing Reedhorn point at the unit they are escaping, which is not an
    // attack. They become combatants only after an explicit player/AI strike.
    if (target.species === 'reedhorn') return provokedByTeam;
    if (target.species === 'mossback-auroch') return provokedByTeam || (target.order?.kind === 'charge' && attackingTeam);
    return Boolean(provokedByTeam || attackingTeam || (camp?.aggroTeamId && camp.aggroTeamId === teamId));
  });
}

function closestResponsiveEnemy(state, unit, range = unit.sight, anchor = null, leash = Infinity) {
  let best = null, bestDistance = range;
  for (const target of defensiveHostiles(state, unit.faction)) {
    if (!factionCurrentlySees(state, unit.faction, target)) continue;
    if (anchor && distance(anchor, target) > leash) continue;
    const d = distance(unit, target) - (target.radius || 0);
    if (d < bestDistance || (d === bestDistance && target.id < best?.id)) { best = target; bestDistance = d; }
  }
  return best;
}

function closestEnemyToAnchor(state, unit, anchor, radius) {
  return closestResponsiveEnemy(state, unit, unit.sight, anchor, radius);
}

function beginEncounter(state, unit, target, resume, anchor = null) {
  if (!target) return false;
  unit.order = {
    kind: 'attack', targetId: target.id, resume: cloneOrder(resume),
    lastKnownX: target.x, lastKnownY: target.y, lastKnownAt: Number(state.elapsed.toFixed(3)), targetVisible: true,
    ...(anchor ? { encounterAnchorX: anchor.x, encounterAnchorY: anchor.y } : {}),
  };
  setActivity(state, unit, 'attacking');
  return true;
}

function applyDamage(state, attacker, target, rawDamage, detail = {}) {
  const attackerMultiplier = state.elapsed < (attacker?.weakenedUntil || 0) ? 0.75 : 1;
  const armor = (target.armor || 0) + (state.elapsed < (target.shieldUntil || 0) ? (target.shieldArmor || 0) : 0);
  const damage = Math.max(1, rawDamage * attackerMultiplier - armor);
  target.hp = Math.max(0, target.hp - damage);
  target.lastHitAt = Number(state.elapsed.toFixed(3));
  target.lastAttackerId = attacker?.id || null;
  emitEvent(state, 'hit', target, { attackerId: attacker?.id || null, faction: attacker?.faction || null, damage, ...detail });
  return damage;
}

function strike(state, attacker, target) {
  const rawDamage = attacker.damage * (state.elapsed < (attacker.inspiredUntil || 0) ? 1.25 : 1);
  const damage = applyDamage(state, attacker, target, rawDamage);
  attacker.cooldown = attacker.attackCooldown;
  state.particles.push({ x: target.x, y: target.y, color: FACTIONS[attacker.faction]?.glow || '#d9f3ff', life: 0.32, type: ['ranged', 'hero'].includes(attacker.role) ? 'projectile-hit' : 'impact' });
  if (target.hp <= 0) {
    attacker.kills += 1;
    awardHeroExperience(state, attacker, target.type === 'guardian' ? 25 : target.type ? 30 : 14);
  }
}

function awardHeroExperience(state, hero, amount) {
  if (!hero || hero.role !== 'hero' || !alive(hero) || amount <= 0) return;
  hero.experience += amount;
  while (hero.level < 5 && hero.experience >= hero.nextLevelXp) {
    hero.level += 1; hero.nextLevelXp = hero.level * 60;
    const previousMax = hero.maxHp;
    hero.maxHp = Math.round(hero.maxHp * 1.08); hero.hp = Math.min(hero.maxHp, hero.hp + (hero.maxHp - previousMax));
    hero.damage = Number((hero.damage * 1.08).toFixed(3));
    emitEvent(state, 'hero-level-up', hero, { faction: hero.faction, heroId: hero.heroId, level: hero.level });
  }
}

function damageFromHeroAbility(state, hero, target, amount) {
  if (!alive(target)) return;
  applyDamage(state, hero, target, amount, { abilityId: hero.ability.id });
  if (target.hp <= 0) { hero.kills += 1; awardHeroExperience(state, hero, target.type === 'guardian' ? 25 : 14); }
}

function updateHeroAbility(state, hero, target) {
  if (!hero.ability) return;
  if (hero.ability.remaining > 0) return;
  const rules = HEROES[hero.faction]?.ability;
  if (!rules || !target || !alive(target) || !factionCurrentlySees(state, hero.faction, target) || distance(hero, target) > rules.range) return;
  const combatVictims = () => defensiveHostiles(state, hero.faction).filter(item => item.id === target.id || item.faction !== 'neutral');
  if (rules.id === 'rallying-cry') {
    for (const ally of state.units.filter(unit => areAllies(state, unit.faction, hero.faction) && alive(unit) && distance(unit, hero) <= rules.range)) ally.inspiredUntil = state.elapsed + rules.duration;
  } else if (rules.id === 'verdant-renewal') {
    for (const ally of [...state.units, ...state.buildings].filter(item => areAllies(state, item.faction, hero.faction) && alive(item) && item.hp < item.maxHp && distance(item, hero) <= rules.range)) ally.hp = Math.min(ally.maxHp, ally.hp + rules.heal);
  } else if (rules.id === 'faultsong') {
    for (const enemy of combatVictims().filter(item => distance(item, hero) <= rules.range)) damageFromHeroAbility(state, hero, enemy, rules.damage);
  } else if (rules.id === 'prism-volley') {
    const victims = combatVictims().filter(item => distance(item, target) <= rules.splash).sort((a, b) => distance(a, target) - distance(b, target) || a.id.localeCompare(b.id)).slice(0, 4);
    for (const enemy of victims) damageFromHeroAbility(state, hero, enemy, rules.damage);
  } else if (rules.id === 'mirelight-veil') {
    const victims = combatVictims().filter(item => distance(item, hero) <= rules.range);
    for (const enemy of victims) { damageFromHeroAbility(state, hero, enemy, rules.damage); enemy.weakenedUntil = state.elapsed + rules.duration; }
  } else if (rules.id === 'updraft-bulwark') {
    for (const ally of [...state.units, ...state.buildings].filter(item => areAllies(state, item.faction, hero.faction) && alive(item) && distance(item, hero) <= rules.range)) {
      ally.shieldUntil = state.elapsed + rules.duration; ally.shieldArmor = Math.max(ally.shieldArmor || 0, rules.armor);
    }
  }
  hero.ability.remaining = rules.cooldown * researchModifiers(state, hero.faction).heroCooldown; hero.ability.lastUsedAt = Number(state.elapsed.toFixed(3));
  emitEvent(state, 'hero-ability', hero, { faction: hero.faction, heroId: hero.heroId, abilityId: rules.id, abilityName: rules.name, targetId: target.id });
}

function retargetWorksite(state, unit, site, kind, slot = null) {
  exitWorksite(state, unit);
  unit.order.targetId = site.id;
  if (kind === 'build') unit.order.phase = 'travel';
  unit.order.slot = Number.isInteger(slot) ? slot : nextWorkSlot(state, site.id, unit.id, kind);
  unit.job.targetId = site.id;
  attachWorksite(state, unit, unit.order);
}

function enterWorksite(state, unit, workingActivity, dt) {
  if (unit.insideWorksite) {
    if (unit.order.kind === 'build') unit.order.phase = 'work';
    setActivity(state, unit, workingActivity); return true;
  }
  if (!unit.workSlot) return false;
  if (unit.order.kind === 'build') unit.order.phase = 'travel';
  setActivity(state, unit, 'moving');
  // Interpolate to the interaction radius before hiding the worker. The
  // assignment itself never changes position; the final slot snap happens on
  // the same tick that work begins and the worker becomes insideWorksite.
  if (!stepToward(state, unit, unit.workSlot, dt, WORKER_JOBS[unit.order.kind]?.interactionRange || 3)) return false;
  unit.x = unit.workSlot.x; unit.y = unit.workSlot.y;
  unit.insideWorksite = true;
  if (unit.order.kind === 'build') unit.order.phase = 'work';
  setActivity(state, unit, workingActivity);
  emitEvent(state, 'worksite-enter', unit, { worksiteId: unit.worksiteId, workSlot: unit.workSlot.index, job: unit.order.kind });
  return true;
}

function updateWorker(state, unit, dt) {
  const order = unit.order;
  if (order.kind === 'gather') {
    let node = state.nodes.find(item => item.id === order.targetId);
    const waitAtDepletedSite = () => {
      if (!Number.isFinite(order.depletedX) || !Number.isFinite(order.depletedY)) {
        const position = node ? gatherPosition(node, order.slot) : unit.workSlot || unit;
        order.depletedX = position.x; order.depletedY = position.y;
      }
      order.phase = 'depleted'; exitWorksite(state, unit, true); setActivity(state, unit, 'moving');
      if (stepToward(state, unit, { x: order.depletedX, y: order.depletedY }, dt, 0)) {
        emitEvent(state, 'resource-depleted-waiting', unit, { faction: unit.faction, nodeId: order.targetId, resourceType: order.resourceType });
        completeUnitOrder(state, unit);
      }
    };
    if (order.phase === 'depleted') { waitAtDepletedSite(); return; }
    if ((!node || !isResourceNodeHarvestable(state, node)) && order.phase !== 'return') {
      if (unit.carryAmount > 0) {
        const position = node ? gatherPosition(node, order.slot) : unit.workSlot || unit;
        order.depletedX = position.x; order.depletedY = position.y;
        order.phase = 'return'; exitWorksite(state, unit, true);
      } else { waitAtDepletedSite(); return; }
    }
    if (order.phase === 'return' || unit.carryAmount >= unit.capacity) {
      order.phase = 'return'; exitWorksite(state, unit, true); setActivity(state, unit, 'returning');
      const dropoff = closestDropoff(state, unit, order.dropoffId);
      if (!dropoff) { completeUnitOrder(state, unit); return; }
      order.dropoffId = dropoff.id;
      if (stepToward(state, unit, dropoff, dt, dropoff.radius + 10)) {
        const faction = factionOf(state, unit.faction);
        if (unit.carryType) faction.stock[unit.carryType] += unit.carryAmount;
        if (unit.faction === state.playerFaction) state.stats.gathered += unit.carryAmount;
        if (unit.carryAmount > 0) emitEvent(state, 'delivery', dropoff, { unitId: unit.id, faction: unit.faction, resourceType: unit.carryType, amount: Number(unit.carryAmount.toFixed(2)) });
        unit.carryAmount = 0; unit.carryType = null;
        node = state.nodes.find(item => item.id === order.targetId && isResourceNodeHarvestable(state, item));
        if (node) {
          if (unit.worksiteId !== node.id) retargetWorksite(state, unit, node, 'gather');
          order.phase = 'collect'; setActivity(state, unit, 'moving');
        } else waitAtDepletedSite();
      }
      return;
    }
    if (!unit.workSlot || unit.worksiteId !== node.id) retargetWorksite(state, unit, node, 'gather', order.slot);
    if (enterWorksite(state, unit, 'gathering', dt)) {
      const faction = FACTIONS[unit.faction], bonus = faction.affinity === node.type ? faction.gatherBonus : 1;
      const amount = Math.min(node.amount, unit.capacity - unit.carryAmount, unit.gatherRate * bonus * dt);
      node.amount -= amount; unit.carryType = node.type; unit.carryAmount += amount;
      if (unit.carryAmount >= unit.capacity - 0.01 || node.amount <= 0) {
        order.phase = 'return'; exitWorksite(state, unit, true); setActivity(state, unit, 'returning');
      }
    }
    return;
  }
  if (order.kind === 'build') {
    const building = state.buildings.find(item => item.id === order.targetId);
    if (!building || !alive(building) || building.complete) { completeUnitOrder(state, unit); return; }
    if (!unit.workSlot || unit.worksiteId !== building.id) retargetWorksite(state, unit, building, 'build', order.slot);
    if (enterWorksite(state, unit, 'building', dt)) {
      const age = factionOf(state, unit.faction)?.age || 1, modifiers = ageModifiers(unit.faction, age), research = researchModifiers(state, unit.faction);
      const seconds = effectiveBuildingProfile(state, building.faction, building.type, building.fortificationLevel || 0).buildTime;
      const assist = workerAssistMultiplier(state, unit, building.id, 'build');
      building.progress = Math.min(1, building.progress + dt * FACTIONS[unit.faction].buildSpeed * modifiers.buildSpeed * research.buildSpeed * assist / seconds);
      building.hp = Math.max(building.hp, building.maxHp * building.progress);
      building.activity = 'constructing';
      if (building.progress >= 1) {
        building.complete = true; building.hp = building.maxHp; building.activity = 'ready';
        for (const worker of state.units.filter(item => item.order?.kind === 'build' && item.order.targetId === building.id)) completeUnitOrder(state, worker);
        emitEvent(state, 'construction-complete', building, { faction: building.faction, buildingType: building.type });
        recalculatePopulation(state);
      }
    }
    return;
  }
  if (order.kind === 'repair') {
    const building = state.buildings.find(item => item.id === order.targetId && item.faction === unit.faction);
    if (!building || !alive(building)) { completeUnitOrder(state, unit); return; }
    if (!building.complete) { activateUnitOrder(state, unit, { kind: 'build', targetId: building.id, phase: 'travel' }, 'build'); return; }
    if (building.hp >= building.maxHp - 0.01) { building.hp = building.maxHp; building.activity = 'ready'; completeUnitOrder(state, unit); return; }
    if (!unit.workSlot || unit.worksiteId !== building.id) retargetWorksite(state, unit, building, 'repair', order.slot);
    if ((order.blockedUntil || 0) > state.elapsed) { setActivity(state, unit, 'repair-blocked'); return; }
    if (enterWorksite(state, unit, 'repairing', dt)) {
      const faction = factionOf(state, unit.faction), rules = WORKER_JOBS.repair;
      const assist = workerAssistMultiplier(state, unit, building.id, 'repair');
      const modifiers = ageModifiers(unit.faction, faction.age || 1), research = researchModifiers(state, unit.faction);
      let hp = Math.min(building.maxHp - building.hp, rules.hpPerSecond * modifiers.repairRate * research.repairRate * assist * dt);
      if (rules.timberPerHp > 0) hp = Math.min(hp, faction.stock.timber / rules.timberPerHp);
      if (rules.stonePerHp > 0) hp = Math.min(hp, faction.stock.stone / rules.stonePerHp);
      if (hp <= 0.0001) {
        order.blockedUntil = state.elapsed + 1;
        exitWorksite(state, unit, true); setActivity(state, unit, 'repair-blocked'); return;
      }
      faction.stock.timber = Math.max(0, faction.stock.timber - hp * rules.timberPerHp);
      faction.stock.stone = Math.max(0, faction.stock.stone - hp * rules.stonePerHp);
      building.hp = Math.min(building.maxHp, building.hp + hp); building.activity = 'repairing';
      if (building.hp >= building.maxHp - 0.01) {
        building.hp = building.maxHp; building.activity = 'ready';
        for (const worker of state.units.filter(item => item.order?.kind === 'repair' && item.order.targetId === building.id)) completeUnitOrder(state, worker);
        emitEvent(state, 'repair-complete', building, { faction: building.faction, buildingType: building.type });
      }
    }
  }
}

function updateUnit(state, unit, dt) {
  if (!alive(unit)) return;
  if (unit.insideGarrison) {
    const building = state.buildings.find(item => item.id === unit.garrisonId);
    if (!alive(building)) exitGarrison(state, unit);
    else { unit.x = building.x; unit.y = building.y; setActivity(state, unit, 'garrisoned'); return; }
  }
  unit.cooldown = Math.max(0, unit.cooldown - dt);
  if (unit.role === 'hero' && unit.ability) unit.ability.remaining = Math.max(0, (unit.ability.remaining || 0) - dt);
  if (unit.role === 'worker' && ['gather', 'build', 'repair'].includes(unit.order.kind)) { updateWorker(state, unit, dt); return; }
  if (unit.order.kind === 'move') {
    setActivity(state, unit, 'moving');
    const encounterRange = unit.order.attackMove ? 175 : unit.role === 'worker' ? 48 : 108;
    const enemy = closestResponsiveEnemy(state, unit, encounterRange);
    if (enemy) beginEncounter(state, unit, enemy, unit.order, { x: unit.x, y: unit.y });
    if (unit.order.kind === 'move' && stepToward(state, unit, unit.order, dt, 4)) completeUnitOrder(state, unit);
    return;
  }
  if (unit.order.kind === 'scout') {
    if (unit.order.phase !== 'survey') {
      setActivity(state, unit, 'moving');
      if (stepToward(state, unit, unit.order, dt, 6)) {
        unit.order.phase = 'survey'; unit.order.surveyRemaining = unit.order.surveyTime || 1.1;
        setActivity(state, unit, 'scouting');
      }
    } else {
      setActivity(state, unit, 'scouting');
      unit.order.surveyRemaining -= dt;
      if (unit.order.surveyRemaining <= 0) {
        emitEvent(state, 'scout-arrived', unit, { faction: unit.faction, unitId: unit.id, targetId: unit.order.targetId || null });
        completeUnitOrder(state, unit);
      }
    }
    return;
  }
  if (unit.order.kind === 'garrison') {
    const building = state.buildings.find(item => item.id === unit.order.targetId && alive(item) && item.complete && areAllies(state, unit.faction, item.faction));
    if (!building || (building.garrisonCapacity || 0) <= 0) { completeUnitOrder(state, unit); return; }
    setActivity(state, unit, 'moving');
    if (stepToward(state, unit, building, dt, building.radius + 4)) {
      building.garrisonIds ||= [];
      if (!building.garrisonIds.includes(unit.id) && building.garrisonIds.length < building.garrisonCapacity) building.garrisonIds.push(unit.id);
      if (building.garrisonIds.includes(unit.id)) {
        unit.insideGarrison = true; unit.garrisonId = building.id; unit.x = building.x; unit.y = building.y; setActivity(state, unit, 'garrisoned');
        emitEvent(state, 'garrison-enter', unit, { faction: unit.faction, buildingId: building.id, buildingFaction: building.faction });
      } else completeUnitOrder(state, unit);
    }
    return;
  }
  if (unit.order.kind === 'defend') {
    const protectedTarget = unit.order.targetId ? getEntity(state, unit.order.targetId) : null;
    if (protectedTarget && alive(protectedTarget)) { unit.order.x = protectedTarget.x; unit.order.y = protectedTarget.y; }
    const anchor = { x: unit.order.x, y: unit.order.y };
    const enemy = closestEnemyToAnchor(state, unit, anchor, unit.order.radius || DEFEND_RADIUS);
    if (enemy) { beginEncounter(state, unit, enemy, unit.order, anchor); return; }
    setActivity(state, unit, distance(unit, anchor) > 14 ? 'moving' : 'guarding');
    if (distance(unit, anchor) > 14) stepToward(state, unit, anchor, dt, 8);
    return;
  }
  if (unit.order.kind === 'patrol') {
    const points = unit.order.points || [];
    if (points.length < 2) { completeUnitOrder(state, unit); return; }
    const destination = points[unit.order.index % points.length];
    const encounterAnchor = { x: unit.x, y: unit.y };
    const enemy = closestResponsiveEnemy(state, unit, 135, encounterAnchor, unit.order.radius || DEFEND_RADIUS);
    if (enemy) { beginEncounter(state, unit, enemy, unit.order, encounterAnchor); return; }
    setActivity(state, unit, 'moving');
    if (stepToward(state, unit, destination, dt, PATROL_ARRIVAL_RADIUS)) unit.order.index = (unit.order.index + 1) % points.length;
    return;
  }
  if (unit.order.kind === 'hold') {
    const enemy = closestResponsiveEnemy(state, unit, unit.range + 55);
    if (enemy && distance(unit, enemy) <= unit.range + (enemy.radius || 0) + 1) {
      beginEncounter(state, unit, enemy, unit.order, { x: unit.x, y: unit.y });
    } else setActivity(state, unit, 'holding');
    return;
  }
  if (unit.order.kind === 'attack') {
    const target = getEntity(state, unit.order.targetId);
    if (!target || !alive(target) || !areEnemies(state, unit.faction, target.faction)) {
      if (unit.order.resume && unit.order.resume.kind !== 'idle') { unit.order = unit.order.resume; setActivity(state, unit, initialActivity(unit.order)); }
      else completeUnitOrder(state, unit);
      return;
    }
    const patrolAnchor = Number.isFinite(unit.order.encounterAnchorX) && Number.isFinite(unit.order.encounterAnchorY)
      ? { x: unit.order.encounterAnchorX, y: unit.order.encounterAnchorY }
      : unit.order.resume?.points?.[unit.order.resume.index % unit.order.resume.points.length];
    if (patrolAnchor && ['move', 'idle'].includes(unit.order.resume?.kind)) {
      const responsiveLeash = unit.order.resume.kind === 'move' && unit.order.resume.attackMove
        ? 260 : unit.role === 'worker' ? 90 : unit.role === 'hero' ? 220 : 190;
      if (distance(target, patrolAnchor) > responsiveLeash) {
        unit.order = unit.order.resume;
        setActivity(state, unit, initialActivity(unit.order));
        return;
      }
    }
    if (unit.order.resume?.kind === 'defend' && distance(target, unit.order.resume) > (unit.order.resume.radius || DEFEND_RADIUS) + 25) {
      unit.order = unit.order.resume; setActivity(state, unit, 'moving'); return;
    }
    if (unit.order.resume?.kind === 'patrol' && patrolAnchor && distance(target, patrolAnchor) > (unit.order.resume.radius || DEFEND_RADIUS) + 25) {
      unit.order = unit.order.resume; setActivity(state, unit, 'moving'); return;
    }
    if (unit.order.resume?.kind === 'hold' && distance(unit, target) > unit.range + (target.radius || 0) + 1) {
      unit.order = unit.order.resume; setActivity(state, unit, 'holding'); return;
    }
    const targetVisible = factionCurrentlySees(state, unit.faction, target);
    if (targetVisible) {
      unit.order.lastKnownX = target.x; unit.order.lastKnownY = target.y;
      unit.order.lastKnownAt = Number(state.elapsed.toFixed(3)); unit.order.targetVisible = true;
    } else {
      unit.order.targetVisible = false;
      const lastKnown = Number.isFinite(unit.order.lastKnownX) && Number.isFinite(unit.order.lastKnownY)
        ? { x: unit.order.lastKnownX, y: unit.order.lastKnownY } : null;
      if (lastKnown && distance(unit, lastKnown) > 9) { setActivity(state, unit, 'moving'); stepToward(state, unit, lastKnown, dt, 8); }
      else if (unit.order.resume && unit.order.resume.kind !== 'idle') { unit.order = unit.order.resume; setActivity(state, unit, initialActivity(unit.order)); }
      else completeUnitOrder(state, unit);
      return;
    }
    if (unit.role === 'hero') updateHeroAbility(state, unit, target);
    const stop = unit.range + (target.radius || 0);
    if (distance(unit, target) > stop) { setActivity(state, unit, 'moving'); stepToward(state, unit, target, dt, stop - 1); }
    else { setActivity(state, unit, 'attacking'); if (unit.cooldown <= 0) strike(state, unit, target); }
    return;
  }
  const enemy = closestResponsiveEnemy(state, unit, unit.role === 'worker' ? 55 : unit.role === 'hero' ? 175 : 135);
  if (enemy) { beginEncounter(state, unit, enemy, { kind: 'idle' }, { x: unit.x, y: unit.y }); if (unit.role === 'hero') updateHeroAbility(state, unit, enemy); }
  else setActivity(state, unit, 'idle');
}

function dispatchFromRally(state, building, unit) {
  const rally = building.rally;
  if (!rally) return;
  const target = rally.targetId ? getEntity(state, rally.targetId) : null;
  if (rally.kind === 'gather' && unit.role === 'worker') {
    const node = target && state.nodes.includes(target) && isResourceNodeHarvestable(state, target) ? target : null;
    if (node) { issueGather(state, building.faction, [unit.id], node.id); return; }
  }
  if (rally.kind === 'attack' && target?.faction && areEnemies(state, building.faction, target.faction) && alive(target)) {
    if (issueAttack(state, building.faction, [unit.id], target.id)) return;
    issueMove(state, building.faction, [unit.id], rally.x, rally.y, true); return;
  }
  if (rally.kind === 'build' && unit.role === 'worker' && target && state.buildings.includes(target) && alive(target) && !target.complete) {
    issueBuild(state, building.faction, [unit.id], target.id); return;
  }
  if (rally.kind === 'repair' && unit.role === 'worker' && target && state.buildings.includes(target) && alive(target) && target.hp < target.maxHp - 0.01) {
    issueRepair(state, building.faction, [unit.id], target.id); return;
  }
  if (rally.kind === 'defend' || (['gather', 'build', 'repair'].includes(rally.kind) && unit.role !== 'worker')) {
    issueDefend(state, building.faction, [unit.id], rally.x, rally.y, target && (alive(target) || state.wells.includes(target)) ? target.id : null); return;
  }
  issueMove(state, building.faction, [unit.id], rally.x, rally.y, false);
}

function updateBuildings(state, dt) {
  for (const building of state.buildings) {
    if (!alive(building) || !building.complete) continue;
    building.cooldown = Math.max(0, building.cooldown - dt); building.defenseCooldown = Math.max(0, (building.defenseCooldown || 0) - dt);
    building.garrisonIds = (building.garrisonIds || []).filter(unitId => state.units.some(unit => unit.id === unitId && alive(unit) && unit.insideGarrison && unit.garrisonId === building.id));
    if (building.type === 'tower' && building.cooldown <= 0) {
      const target = defensiveHostiles(state, building.faction).sort((a, b) => distance(a, building) - distance(b, building) || a.id.localeCompare(b.id))[0];
      const profile = effectiveBuildingProfile(state, building.faction, building.type, building.fortificationLevel || 0);
      if (target && distance(target, building) <= profile.range) {
        const damage = applyDamage(state, building, target, profile.damage); building.cooldown = profile.cooldown;
        state.particles.push({ x: target.x, y: target.y, color: FACTIONS[building.faction].glow, life: 0.4, type: 'tower-hit' });
      }
    }
    if (building.garrisonIds.length && building.defenseCooldown <= 0) {
      const occupants = building.garrisonIds.map(unitId => state.units.find(unit => unit.id === unitId)).filter(alive);
      const defenseRange = building.type === 'tower' ? effectiveBuildingProfile(state, building.faction, 'tower', building.fortificationLevel || 0).range : building.type === 'heart' ? 175 : 135;
      const target = defensiveHostiles(state, building.faction).filter(item => distance(item, building) <= defenseRange).sort((a, b) => distance(a, building) - distance(b, building) || a.id.localeCompare(b.id))[0];
      if (target) {
        const rawDamage = occupants.reduce((sum, unit) => sum + unit.damage * (unit.role === 'ranged' || unit.role === 'hero' ? 0.42 : 0.3), 0) + (building.fortificationLevel || 0) * 2;
        const damage = applyDamage(state, building, target, rawDamage, { garrison: true }); building.defenseCooldown = 1.15;
        state.particles.push({ x: target.x, y: target.y, color: FACTIONS[building.faction].glow, life: 0.42, type: 'defensive-shot' });
        emitEvent(state, 'defensive-shot', building, { faction: building.faction, targetId: target.id, damage, garrisonCount: occupants.length, fortificationLevel: building.fortificationLevel || 0 });
      }
    }
    if (!building.queue.length) { if (building.activity === 'training' && !building.researching) building.activity = 'ready'; building.productionProgress = 0; continue; }
    building.activity = 'training';
    building.queue[0].remaining -= dt;
    building.productionProgress = clamp(1 - building.queue[0].remaining / building.queue[0].total, 0, 1);
    if (building.queue[0].remaining <= 0) {
      const item = building.queue.shift(), angle = (state.nextId * 2.399) % TAU;
      const unit = addUnit(state, building.faction, item.role, building.x + Math.cos(angle) * (building.radius + 24), building.y + Math.sin(angle) * (building.radius + 24));
      building.lastSpawnId = unit.id; building.lastSpawnAt = Number(state.elapsed.toFixed(3));
      dispatchFromRally(state, building, unit);
      emitEvent(state, 'unit-trained', building, { faction: building.faction, unitId: unit.id, role: item.role });
      if (item.role === 'hero') emitEvent(state, 'hero-recruited', unit, { faction: unit.faction, unitId: unit.id, heroId: unit.heroId, heroName: unit.name });
      if (building.faction === state.playerFaction) state.stats.trained += 1;
      building.productionProgress = 0; building.activity = building.queue.length ? 'training' : 'ready';
      recalculatePopulation(state);
    }
  }
}

function setWorldEntityActivity(state, entity, activity) {
  if (entity.activity !== activity) { entity.activity = activity; entity.activityStartedAt = Number(state.elapsed.toFixed(3)); }
}

function updateGuardians(state, dt) {
  for (const guardian of state.guardians || []) {
    if (!alive(guardian)) continue;
    guardian.cooldown = Math.max(0, guardian.cooldown - dt);
    const camp = state.camps.find(item => item.id === guardian.campId), leash = (camp?.radius || 54) + GUARDIAN_CAMP_LEASH_PADDING;
    let target = guardian.order?.targetId ? getEntity(state, guardian.order.targetId) : null;
    if (!alive(target) || target.faction === 'neutral' || (camp && distance(target, camp) > leash)) target = null;
    if (!target) {
      target = state.units.filter(unit => alive(unit) && unit.faction !== 'neutral' && (!camp || distance(unit, camp) <= leash) && distance(unit, guardian) <= guardian.sight)
        .sort((a, b) => distance(a, guardian) - distance(b, guardian) || a.id.localeCompare(b.id))[0] || null;
      guardian.order = target ? { kind: 'attack', targetId: target.id } : { kind: 'guard', targetId: null };
      if (target && camp) { camp.aggroFaction = target.faction; camp.aggroTeamId = factionOf(state, target.faction)?.teamId || null; camp.activity = 'engaged'; }
    }
    if (target) {
      const stop = guardian.range + (target.radius || 0);
      if (distance(guardian, target) > stop) { setWorldEntityActivity(state, guardian, 'pursuing'); stepToward(state, guardian, target, dt, stop - 1); }
      else if (guardian.cooldown <= 0) { setWorldEntityActivity(state, guardian, 'attacking'); strike(state, guardian, target); }
      else setWorldEntityActivity(state, guardian, 'attacking');
    } else {
      const home = camp || { x: guardian.homeX, y: guardian.homeY };
      if (distance(guardian, home) > (camp?.radius || 35)) { setWorldEntityActivity(state, guardian, 'returning'); stepToward(state, guardian, { x: guardian.homeX, y: guardian.homeY }, dt, 5); }
      else setWorldEntityActivity(state, guardian, 'guarding');
    }
  }
}

function faunaThreat(state, animal) {
  const attacker = animal.lastAttackerId ? getEntity(state, animal.lastAttackerId) : null;
  if (alive(attacker) && attacker.faction !== 'neutral' && distance(animal, attacker) <= animal.sight + 120) return attacker;
  return state.units.filter(unit => alive(unit) && unit.faction !== 'neutral' && distance(unit, animal) <= animal.sight
    && (animal.species === 'reedhorn' || unit.order?.targetId === animal.id))
    .sort((a, b) => distance(a, animal) - distance(b, animal) || a.id.localeCompare(b.id))[0] || null;
}

function updateFauna(state, dt) {
  const world = worldOf(state);
  for (const animal of state.fauna || []) {
    if (!alive(animal)) continue;
    animal.cooldown = Math.max(0, animal.cooldown - dt);
    const directThreat = faunaThreat(state, animal);
    if (directThreat && animal.species === 'mossback-auroch') {
      animal.order = { kind: 'charge', targetId: directThreat.id };
      const stop = animal.range + (directThreat.radius || 0);
      if (distance(animal, directThreat) > stop) { setWorldEntityActivity(state, animal, 'charging'); stepToward(state, animal, directThreat, dt, stop - 1); }
      else if (animal.cooldown <= 0) { setWorldEntityActivity(state, animal, 'attacking'); strike(state, animal, directThreat); }
      continue;
    }
    if (directThreat && animal.species === 'reedhorn') {
      animal.fleeUntil = Math.max(Number(animal.fleeUntil) || 0, state.elapsed + .75); animal.fleeFromX = directThreat.x; animal.fleeFromY = directThreat.y;
    }
    const threat = directThreat || (animal.species === 'reedhorn' && state.elapsed < (Number(animal.fleeUntil) || 0) && Number.isFinite(animal.fleeFromX) && Number.isFinite(animal.fleeFromY) ? { id: null, x: animal.fleeFromX, y: animal.fleeFromY } : null);
    if (threat) {
      const dx = animal.x - threat.x, dy = animal.y - threat.y, d = Math.max(1, Math.hypot(dx, dy));
      const destination = { x: clamp(animal.x + dx / d * 150, 20, world.width - 20), y: clamp(animal.y + dy / d * 150, 20, world.height - 20) };
      animal.order = { kind: 'flee', targetId: threat.id, x: destination.x, y: destination.y };
      setWorldEntityActivity(state, animal, 'fleeing'); stepToward(state, animal, destination, dt, 4);
      continue;
    }
    if (animal.activity === 'fleeing') {
      setWorldEntityActivity(state, animal, 'grazing'); animal.grazeUntil = state.elapsed + 1.8; animal.order = { kind: 'graze', x: animal.x, y: animal.y }; continue;
    }
    if (animal.activity === 'grazing' && state.elapsed < (Number(animal.grazeUntil) || 0)) {
      animal.order = { kind: 'graze', x: animal.x, y: animal.y }; continue;
    }
    const migrationRadius = animal.species === 'reedhorn' ? 85 : 48;
    const destination = { x: clamp(animal.homeX + Math.cos(animal.waypointAngle) * migrationRadius, 20, world.width - 20), y: clamp(animal.homeY + Math.sin(animal.waypointAngle) * migrationRadius, 20, world.height - 20) };
    animal.order = { kind: 'graze', x: destination.x, y: destination.y };
    if (distance(animal, destination) <= 5) {
      animal.waypointAngle = (animal.waypointAngle + 1.37) % TAU; setWorldEntityActivity(state, animal, 'grazing');
      animal.grazeUntil = state.elapsed + 2.2 + ((Number(String(animal.id).match(/\d+/)?.[0]) || 0) % 5) * .18; animal.order = { kind: 'graze', x: animal.x, y: animal.y };
    } else { setWorldEntityActivity(state, animal, 'migrating'); stepToward(state, animal, destination, dt * (animal.species === 'reedhorn' ? .14 : .16), 4); }
  }
}

function updateExploration(state) {
  updateExploredCells(state);
  for (const camp of state.camps || []) {
    camp.discoveredBy ||= {};
    camp.discoveredByTeams ||= {};
    for (const faction of state.factions) {
      if (camp.discoveredBy[faction.id] !== undefined || faction.defeated) continue;
      const scout = state.units.filter(unit => unit.faction === faction.id && alive(unit) && distance(unit, camp) <= (unit.sight || 180) + camp.radius)
        .sort((a, b) => distance(a, camp) - distance(b, camp) || a.id.localeCompare(b.id))[0];
      if (!scout) continue;
      const discoveredAt = Number(state.elapsed.toFixed(3)); camp.discoveredBy[faction.id] = discoveredAt;
      if (camp.discoveredByTeams[faction.teamId] === undefined) camp.discoveredByTeams[faction.teamId] = discoveredAt;
      if (!faction.exploredCamps.includes(camp.id)) faction.exploredCamps.push(camp.id);
      faction.knowledge = (faction.knowledge || 0) + 8;
      emitEvent(state, 'camp-discovered', camp, { faction: faction.id, teamId: faction.teamId, campId: camp.id, scoutId: scout.id });
      if (faction.id === state.playerFaction) state.stats.discoveries += 1;
    }
  }
  for (const landmark of state.landmarks || []) {
    landmark.discoveredBy ||= {};
    landmark.discoveredByTeams ||= {};
    for (const faction of state.factions) {
      if (landmark.discoveredBy[faction.id] !== undefined || faction.defeated) continue;
      const scout = state.units.filter(unit => unit.faction === faction.id && alive(unit) && distance(unit, landmark) <= (unit.sight || 180) + landmark.radius)
        .sort((a, b) => distance(a, landmark) - distance(b, landmark) || a.id.localeCompare(b.id))[0];
      if (!scout) continue;
      const discoveredAt = Number(state.elapsed.toFixed(3)); landmark.discoveredBy[faction.id] = discoveredAt;
      if (landmark.discoveredByTeams[faction.teamId] === undefined) landmark.discoveredByTeams[faction.teamId] = discoveredAt;
      landmark.activity = 'discovered'; faction.knowledge = (faction.knowledge || 0) + 10;
      emitEvent(state, 'landmark-discovered', landmark, { faction: faction.id, teamId: faction.teamId, landmarkId: landmark.id, scoutId: scout.id, knowledge: 10 });
    }
  }
  for (const region of state.regions || []) {
    region.discoveredBy ||= {};
    region.discoveredByTeams ||= {};
    for (const faction of state.factions) {
      if (region.discoveredBy[faction.id] !== undefined || faction.defeated) continue;
      const scout = state.units.filter(unit => unit.faction === faction.id && alive(unit) && distance(unit, region) <= (unit.sight || 180) + region.radius)
        .sort((a, b) => distance(a, region) - distance(b, region) || a.id.localeCompare(b.id))[0];
      if (!scout) continue;
      const discoveredAt = Number(state.elapsed.toFixed(3)); region.discoveredBy[faction.id] = discoveredAt;
      if (region.discoveredByTeams[faction.teamId] === undefined) region.discoveredByTeams[faction.teamId] = discoveredAt;
      if (!faction.exploredRegions.includes(region.id)) faction.exploredRegions.push(region.id);
      faction.knowledge = (faction.knowledge || 0) + 3;
      emitEvent(state, 'region-discovered', region, { faction: faction.id, teamId: faction.teamId, regionId: region.id, scoutId: scout.id, knowledge: 3 });
      if (faction.id === state.playerFaction) state.stats.regionsDiscovered += 1;
    }
  }
}

function updateContacts(state) {
  if (state.contactState?.firstContactAt !== null) return;
  const scouts = state.units.filter(unit => alive(unit) && !unit.insideGarrison).sort((a, b) => a.id.localeCompare(b.id));
  for (const scout of scouts) {
    const contact = [...state.units, ...state.buildings].filter(entity => alive(entity) && areEnemies(state, scout.faction, entity.faction) && distance(scout, entity) <= (scout.sight || 180) + (entity.radius || 0))
      .sort((a, b) => distance(scout, a) - distance(scout, b) || a.id.localeCompare(b.id))[0];
    if (!contact) continue;
    const at = Number(state.elapsed.toFixed(3)), scoutTeamId = factionOf(state, scout.faction)?.teamId, contactTeamId = factionOf(state, contact.faction)?.teamId;
    state.contactState ||= { firstContactAt: null, pairs: {} }; state.contactState.firstContactAt = at;
    const pairId = [scoutTeamId, contactTeamId].sort().join(':'); state.contactState.pairs[pairId] = at;
    emitEvent(state, 'first-contact', contact, { faction: scout.faction, teamId: scoutTeamId, opposingFaction: contact.faction, opposingTeamId: contactTeamId, scoutId: scout.id });
    return;
  }
}

function updateTrade(state, dt) {
  for (const route of state.tradeRoutes || []) {
    if (route.active === false) continue;
    const source = getEntity(state, route.sourceId), target = getEntity(state, route.targetId), faction = factionOf(state, route.faction);
    if (!source?.complete || !target?.complete || !alive(source) || !alive(target) || !faction || faction.defeated || !areAllies(state, route.faction, target.faction)) {
      route.active = false; route.brokenAt = Number(state.elapsed.toFixed(3));
      emitEvent(state, 'trade-route-broken', source || target, { faction: route.faction, teamId: route.teamId, routeId: route.id, sourceId: route.sourceId, targetId: route.targetId });
      continue;
    }
    route.progress = (route.progress || 0) + dt;
    if (route.progress + 1e-9 < route.cycleSeconds) continue;
    route.progress -= route.cycleSeconds;
    const modifiers = researchModifiers(state, route.faction), gross = (TRADE_RULES.baseYield + Math.min(TRADE_RULES.maximumDistanceBonus, route.distance * TRADE_RULES.distanceYield)) * modifiers.tradeRate;
    const income = {
      rations: Math.round(gross), timber: Math.round(gross * 0.62), stone: Math.round(gross * 0.36),
      lumen: faction.age >= 3 ? Math.max(1, Math.round(gross * 0.12)) : 0,
    };
    for (const [resource, amount] of Object.entries(income)) faction.stock[resource] = (faction.stock[resource] || 0) + amount;
    const partner = target.faction !== route.faction ? factionOf(state, target.faction) : null;
    if (partner && !partner.defeated) for (const [resource, amount] of Object.entries(income)) partner.stock[resource] = (partner.stock[resource] || 0) + Math.floor(amount * TRADE_RULES.alliedPartnerShare);
    const total = Object.values(income).reduce((sum, amount) => sum + amount, 0);
    route.income = income; route.deliveries = (route.deliveries || 0) + 1; route.totalIncome = (route.totalIncome || 0) + total; route.lastDeliveryAt = Number(state.elapsed.toFixed(3));
    faction.tradeIncome = (faction.tradeIncome || 0) + total; faction.knowledge = (faction.knowledge || 0) + Math.max(2, total * 0.18);
    if (route.teamId === state.playerTeamId) state.stats.tradeIncome += total;
    emitEvent(state, 'trade-delivery', target, { faction: route.faction, teamId: route.teamId, routeId: route.id, sourceId: route.sourceId, targetId: route.targetId, partnerFaction: route.partnerFaction, income, total });
  }
}

function updateKnowledge(state, dt) {
  for (const faction of state.factions) {
    if (faction.defeated) continue;
    const sources = state.buildings.filter(building => building.faction === faction.id && alive(building) && building.complete);
    const baseRate = sources.reduce((sum, building) => sum + (building.knowledgeRate || 0), 0) + (faction.researches || []).length * 0.002;
    const modifiers = researchModifiers(state, faction.id); faction.knowledgeRate = baseRate * modifiers.knowledgeRate; faction.knowledge = (faction.knowledge || 0) + faction.knowledgeRate * dt;
    let unlocked = false;
    for (const technology of Object.values(TECHNOLOGIES).sort((a, b) => a.threshold - b.threshold || a.id.localeCompare(b.id))) {
      if ((faction.technologies || []).includes(technology.id) || faction.age < technology.age || faction.knowledge + 1e-9 < technology.threshold) continue;
      faction.technologies.push(technology.id); faction.upgrades ||= []; if (!faction.upgrades.includes(technology.name)) faction.upgrades.push(technology.name);
      unlocked = true; if (faction.teamId === state.playerTeamId) state.stats.technologies += 1;
      const heart = state.buildings.find(building => building.faction === faction.id && building.type === 'heart' && alive(building));
      emitEvent(state, 'technology-unlocked', heart, { faction: faction.id, teamId: faction.teamId, technologyId: technology.id, technologyName: technology.name, threshold: technology.threshold, effects: technology.effects });
    }
    const next = Object.values(TECHNOLOGIES).filter(technology => !(faction.technologies || []).includes(technology.id)).sort((a, b) => a.threshold - b.threshold || a.id.localeCompare(b.id))[0];
    faction.technologyProgress = next ? { id: next.id, name: next.name, threshold: next.threshold, current: faction.knowledge, age: next.age, ageLocked: faction.age < next.age } : null;
    if (unlocked) refreshFactionStats(state, faction);
  }
}

function updateTerritories(state, dt) {
  const regions = state.regions || []; if (!regions.length) return;
  state.influenceZones = state.buildings.filter(building => alive(building) && building.complete && ['heart', 'settlement'].includes(building.type)).map(building => {
    const modifiers = researchModifiers(state, building.faction), radius = (building.influenceRadius || worldOf(state).settlementInfluence || 500) * modifiers.influence;
    return { id: `influence-${building.id}`, type: 'influence-zone', sourceId: building.id, sourceType: building.type, faction: building.faction, teamId: factionOf(state, building.faction)?.teamId || null, x: building.x, y: building.y, radius, strength: building.type === 'heart' ? 1.2 : 1 };
  });
  for (const region of regions) {
    const teams = new Map(), factions = new Map();
    for (const zone of state.influenceZones) {
      const reach = zone.radius + region.radius * 0.35, d = distance(zone, region); if (d >= reach) continue;
      const contribution = zone.strength * (1 - d / reach);
      teams.set(zone.teamId, (teams.get(zone.teamId) || 0) + contribution); factions.set(zone.faction, (factions.get(zone.faction) || 0) + contribution);
    }
    region.influence = Object.fromEntries([...teams.entries()].map(([teamId, value]) => [teamId, Number(value.toFixed(4))]));
    const ranked = [...teams.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    const leader = ranked.length && (!ranked[1] || ranked[0][1] > ranked[1][1] + 0.02) ? ranked[0][0] : null;
    const previous = region.controllingTeamId; region.contested = ranked.length > 1 && !leader; region.controllingTeamId = leader;
    region.controllingFaction = leader ? [...factions.entries()].filter(([factionId]) => factionOf(state, factionId)?.teamId === leader).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || null : null;
    if (leader !== previous) {
      region.controlledAt = leader ? Number(state.elapsed.toFixed(3)) : null;
      emitEvent(state, 'region-control-changed', region, { teamId: leader, faction: region.controllingFaction, previousTeamId: previous, contested: region.contested });
    }
  }
  for (const team of state.teams) {
    team.territoryCount = regions.filter(region => region.controllingTeamId === team.id).length; team.territoryShare = team.territoryCount / regions.length;
    for (const faction of state.factions.filter(item => item.teamId === team.id)) faction.territoryCount = regions.filter(region => region.controllingFaction === faction.id).length;
    const eligible = state.elapsed >= (worldOf(state).dominationUnlock || Infinity) && team.territoryShare >= (worldOf(state).dominationShare || 1) && state.factions.filter(item => item.teamId === team.id && !item.defeated).some(item => item.age >= 5);
    team.dominationSeconds = eligible ? (team.dominationSeconds || 0) + dt : Math.max(0, (team.dominationSeconds || 0) - dt * 0.5);
    if (eligible && team.dominationSeconds >= (worldOf(state).dominationHoldSeconds || Infinity) && !state.outcome) {
      state.winnerTeamId = team.id; state.outcome = team.id === state.playerTeamId ? 'victory' : 'defeat';
      emitEvent(state, 'team-victory', null, { teamId: team.id, reason: 'territorial-domination', territoryCount: team.territoryCount, territoryShare: team.territoryShare });
    }
  }
}

function expeditionStageSatisfied(state, expedition, stage, hero) {
  if (stage.kind === 'scout-landmark') return getEntity(state, stage.targetId)?.discoveredBy?.[expedition.faction] !== undefined;
  if (stage.kind === 'clear-camp') return Boolean(getEntity(state, stage.targetId)?.cleared && getEntity(state, stage.targetId)?.rewardTeamId === expedition.teamId);
  if (stage.kind === 'hunt-herd') {
    stage.progress = (state.fauna || []).filter(animal => animal.herdId === stage.herdId && animal.deathHandled && animal.huntedByTeamId === expedition.teamId).length;
    return stage.progress >= (stage.required || 1);
  }
  if (stage.kind === 'recover-treasure') { const camp = getEntity(state, stage.targetId); return Boolean(camp && !camp.treasureRemaining && camp.rewardTeamId === expedition.teamId); }
  if (stage.kind === 'return-home') { const home = getEntity(state, stage.targetId); return Boolean(home && alive(home) && distance(hero, home) <= (home.radius || 40) + 28); }
  return false;
}

function updateExpeditions(state) {
  for (const expedition of state.expeditions || []) {
    if (expedition.status !== 'active') continue;
    const faction = factionOf(state, expedition.faction), hero = state.units.find(unit => unit.id === expedition.heroUnitId);
    if (!faction || faction.defeated || !alive(hero)) {
      expedition.status = 'failed'; expedition.completedAt = Number(state.elapsed.toFixed(3)); if (faction?.expeditionId === expedition.id) faction.expeditionId = null;
      if (hero) { hero.expeditionStatus = 'failed'; hero.expeditionStage = null; }
      emitEvent(state, 'expedition-failed', hero, { faction: expedition.faction, teamId: expedition.teamId, expeditionId: expedition.id, reason: 'hero-lost' });
      continue;
    }
    while (expedition.currentStage && expeditionStageSatisfied(state, expedition, expedition.currentStage, hero)) {
      const completed = expedition.currentStage; completed.complete = true; completed.completedAt = Number(state.elapsed.toFixed(3));
      emitEvent(state, 'expedition-stage-complete', getEntity(state, completed.targetId), { faction: expedition.faction, teamId: expedition.teamId, expeditionId: expedition.id, stageIndex: expedition.stageIndex, stageKind: completed.kind, targetId: completed.targetId });
      expedition.stageIndex += 1; expedition.currentStage = expedition.stages[expedition.stageIndex] || null;
      if (!expedition.currentStage) {
        expedition.status = 'complete'; expedition.completedAt = Number(state.elapsed.toFixed(3)); faction.expeditionId = null;
        hero.expeditionId = null; hero.expeditionStage = null; hero.expeditionStatus = 'complete';
        const reward = expedition.reward || {};
        for (const resource of RESOURCE_TYPES) faction.stock[resource] = (faction.stock[resource] || 0) + (reward[resource] || 0);
        faction.knowledge = (faction.knowledge || 0) + (reward.knowledge || 0); awardHeroExperience(state, hero, reward.heroExperience || 0);
        emitEvent(state, 'expedition-complete', hero, { faction: expedition.faction, teamId: expedition.teamId, expeditionId: expedition.id, questId: expedition.questId, questName: expedition.name, reward: { ...reward } });
        break;
      }
      hero.expeditionStage = expedition.currentStage.kind;
      emitEvent(state, 'expedition-stage', getEntity(state, expedition.currentStage.targetId), { faction: expedition.faction, teamId: expedition.teamId, expeditionId: expedition.id, stageIndex: expedition.stageIndex, stageKind: expedition.currentStage.kind, targetId: expedition.currentStage.targetId });
    }
  }
}

function killerFaction(state, entity) {
  const attacker = entity.lastAttackerId ? getEntity(state, entity.lastAttackerId) : null;
  return attacker?.faction && attacker.faction !== 'neutral' ? attacker.faction : null;
}

function resolveNeutralDeaths(state) {
  for (const guardian of state.guardians || []) {
    if (guardian.hp > 0 || guardian.deathHandled) continue;
    guardian.deathHandled = true; guardian.activity = 'defeated';
    const killer = killerFaction(state, guardian);
    emitEvent(state, 'guardian-defeated', guardian, { campId: guardian.campId, guardianRole: guardian.guardianRole, killerFaction: killer, killerTeamId: factionOf(state, killer)?.teamId || null });
  }
  for (const camp of state.camps || []) {
    if (camp.cleared || !camp.guardianIds.length || camp.guardianIds.some(guardianId => alive(state.guardians.find(item => item.id === guardianId)))) continue;
    const fallen = camp.guardianIds.map(guardianId => state.guardians.find(item => item.id === guardianId)).filter(Boolean);
    const rewardFaction = [...fallen].reverse().map(guardian => killerFaction(state, guardian)).find(Boolean) || camp.aggroFaction;
    const rewardTeamId = factionOf(state, rewardFaction)?.teamId || null;
    camp.cleared = true; camp.clearedAt = Number(state.elapsed.toFixed(3)); camp.rewardFaction = rewardFaction || null; camp.rewardTeamId = rewardTeamId; camp.activity = 'cleared';
    emitEvent(state, 'camp-cleared', camp, { campId: camp.id, campKind: camp.campKind, faction: rewardFaction || null, teamId: rewardTeamId });
    if (resourceEnclaveCamp(camp)) {
      for (const node of state.nodes.filter(item => item.enclaveId === camp.id)) node.guarded = false;
      emitEvent(state, 'resource-enclave-unlocked', camp, { campId: camp.id, nodeIds: [...(camp.resourceNodeIds || [])], faction: rewardFaction || null, teamId: rewardTeamId });
    }
    if (camp.treasureRemaining && rewardFaction && factionOf(state, rewardFaction)) {
      const faction = factionOf(state, rewardFaction), multiplier = ageModifiers(rewardFaction, faction.age || 1).treasureBonus;
      const reward = Object.fromEntries(Object.entries(camp.treasure).map(([resource, amount]) => [resource, Math.round(amount * multiplier)]));
      for (const [resource, amount] of Object.entries(reward)) faction.stock[resource] = (faction.stock[resource] || 0) + amount;
      faction.knowledge = (faction.knowledge || 0) + 24 + camp.level * 6;
      camp.treasureRemaining = false; camp.treasureReward = reward;
      emitEvent(state, 'treasure-collected', camp, { campId: camp.id, faction: rewardFaction, teamId: rewardTeamId, treasure: reward });
      const hero = state.units.find(unit => unit.faction === rewardFaction && unit.role === 'hero' && alive(unit));
      if (hero) awardHeroExperience(state, hero, 40);
      if (rewardTeamId === state.playerTeamId) { state.stats.campsCleared += 1; state.stats.treasure += Object.values(reward).reduce((sum, amount) => sum + amount, 0); }
    }
  }
  for (const animal of state.fauna || []) {
    if (animal.hp > 0 || animal.deathHandled) continue;
    animal.deathHandled = true; animal.activity = 'fallen';
    const factionId = killerFaction(state, animal), faction = factionId && factionOf(state, factionId);
    const rationYield = faction ? Math.round(animal.rationYield * researchModifiers(state, factionId).huntYield) : 0;
    if (faction) faction.stock.rations += rationYield;
    const teamId = faction?.teamId || null;
    animal.huntedByFaction = factionId || null; animal.huntedByTeamId = teamId; animal.huntedAt = Number(state.elapsed.toFixed(3));
    emitEvent(state, 'fauna-hunted', animal, { species: animal.species, herdId: animal.herdId, faction: factionId || null, teamId, rations: rationYield });
    if (teamId === state.playerTeamId) { state.stats.hunted += 1; state.stats.huntRations += rationYield; }
  }
}

function nearestNode(state, point, type) {
  return state.nodes.filter(node => isResourceNodeHarvestable(state, node) && (!type || node.type === type)).sort((a, b) => distance(point, a) - distance(point, b))[0];
}

function aiBuild(state, faction, type, angleOffset) {
  const heart = state.buildings.find(item => item.faction === faction.id && item.type === 'heart' && alive(item));
  const worker = state.units.find(item => item.faction === faction.id && item.role === 'worker' && alive(item) && !['build', 'repair'].includes(item.order.kind));
  if (!heart || !worker) return false;
  for (let attempt = 0; attempt < 8; attempt++) {
    const angle = angleOffset + attempt * 0.72;
    const radius = type === 'settlement' ? (worldOf(state).longSession ? 620 + (attempt % 4) * 150 : 310 + (attempt % 4) * 70) : 105 + (attempt % 3) * 30;
    const x = heart.x + Math.cos(angle) * radius, y = heart.y + Math.sin(angle) * radius;
    const placed = placeBuilding(state, faction.id, type, x, y, [worker.id]);
    if (placed) return true;
  }
  return false;
}

function aiPreferredResource(faction, counts) {
  const targets = { rations: 275, timber: 235, stone: 195, lumen: faction.age >= 2 ? 58 : 34 };
  return RESOURCE_TYPES.slice().sort((a, b) => {
    const affinityA = FACTIONS[faction.id].affinity === a ? 14 : 0, affinityB = FACTIONS[faction.id].affinity === b ? 14 : 0;
    const scoreA = targets[a] - faction.stock[a] + affinityA - (counts[a] || 0) * 42;
    const scoreB = targets[b] - faction.stock[b] + affinityB - (counts[b] || 0) * 42;
    return scoreB - scoreA || a.localeCompare(b);
  })[0];
}

function unitHasOrderKind(unit, kind) {
  return unit.order?.kind === kind || unit.order?.resume?.kind === kind || (unit.orderQueue || []).some(item => item.order?.kind === kind);
}

function runAi(state, faction, dt) {
  faction.aiClock -= dt; faction.waveClock -= dt; faction.rebalanceClock = (faction.rebalanceClock ?? 5) - dt;
  faction.campClock = (faction.campClock ?? 18) - dt; faction.huntClock = (faction.huntClock ?? 13) - dt; faction.fortifyClock = (faction.fortifyClock ?? 45) - dt;
  if (faction.aiClock > 0 || faction.defeated) return;
  faction.aiClock = 1.15;
  const world = worldOf(state), longSession = Boolean(world.longSession);
  const aggressionPhase = !longSession ? 'war' : state.elapsed < (world.contactDelay || 0) ? 'forage'
    : faction.age < 2 ? 'frontier' : faction.age < 3 ? 'contest' : faction.age < 5 || state.elapsed < (world.dominationUnlock || Infinity) ? 'war' : 'domination';
  faction.aggressionPhase = aggressionPhase;
  const units = state.units.filter(unit => unit.faction === faction.id && alive(unit));
  const workers = units.filter(unit => unit.role === 'worker');
  const army = units.filter(unit => unit.role !== 'worker');
  const damaged = state.buildings.filter(item => item.faction === faction.id && alive(item) && item.complete && item.hp < item.maxHp * 0.72)
    .sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp || a.id.localeCompare(b.id))[0];
  if (damaged && !workers.some(unit => unit.order.kind === 'repair' && unit.order.targetId === damaged.id)) {
    const repairer = workers.filter(unit => !['build', 'repair'].includes(unit.order.kind)).sort((a, b) => distance(a, damaged) - distance(b, damaged) || a.id.localeCompare(b.id))[0];
    if (repairer) issueRepair(state, faction.id, [repairer.id], damaged.id);
  }
  const gatherCounts = { rations: 0, timber: 0, stone: 0, lumen: 0 };
  for (const worker of workers) if (worker.order.kind === 'gather' && gatherCounts[worker.order.resourceType] !== undefined) gatherCounts[worker.order.resourceType] += 1;
  for (const worker of workers.filter(unit => unit.order.kind === 'idle')) {
    const preferred = aiPreferredResource(faction, gatherCounts);
    const node = nearestNode(state, worker, preferred) || nearestNode(state, worker);
    if (node) { issueGather(state, faction.id, [worker.id], node.id); gatherCounts[preferred] += 1; }
  }
  if (faction.rebalanceClock <= 0) {
    faction.rebalanceClock = 7 + faction.slot * 0.75;
    const preferred = aiPreferredResource(faction, gatherCounts);
    const candidate = workers.filter(unit => unit.order.kind === 'gather' && unit.order.resourceType !== preferred)
      .sort((a, b) => faction.stock[b.order.resourceType] - faction.stock[a.order.resourceType] || a.id.localeCompare(b.id))[0];
    const node = candidate && (nearestNode(state, candidate, preferred) || nearestNode(state, candidate));
    if (candidate && node) issueGather(state, faction.id, [candidate.id], node.id);
  }
  const heart = state.buildings.find(item => item.faction === faction.id && item.type === 'heart' && alive(item));
  const queuedWorkers = heart?.queue?.filter(item => item.role === 'worker').length || 0;
  if (workers.length + queuedWorkers < 7 && heart) queueUnit(state, faction.id, heart.id, 'worker');
  const activeDepots = state.buildings.filter(item => item.faction === faction.id && item.type === 'depot' && alive(item)).length;
  if (!activeDepots && state.elapsed >= (longSession ? 15 : 8)) aiBuild(state, faction, 'depot', faction.slot === 1 ? 1.55 : 1.7);
  const completedDepot = state.buildings.some(item => item.faction === faction.id && item.type === 'depot' && alive(item) && item.complete);
  const activeDwellings = state.buildings.filter(item => item.faction === faction.id && item.type === 'dwelling' && alive(item)).length;
  if (completedDepot && faction.capacity - faction.population <= 3 && activeDwellings < 5) aiBuild(state, faction, 'dwelling', (faction.slot === 1 ? 2.35 : .55) + activeDwellings * .71);
  let barracks = state.buildings.find(item => item.faction === faction.id && item.type === 'barracks' && alive(item));
  if (completedDepot && !barracks && (!longSession || state.elapsed >= (world.aiBarracksAt || 0))) aiBuild(state, faction, 'barracks', faction.slot === 1 ? 0.9 : 2.2);
  barracks = state.buildings.find(item => item.faction === faction.id && item.type === 'barracks' && alive(item) && item.complete);
  if (barracks && (!barracks.rally || barracks.rally.strategicPhase !== aggressionPhase)) {
    const objective = aggressionPhase === 'forage' || aggressionPhase === 'frontier' ? heart
      : state.wells.filter(well => well.ownerTeamId !== faction.teamId).sort((a, b) => distance(barracks, a) - distance(barracks, b) || a.id.localeCompare(b.id))[0];
    if (objective) { setRallyPoint(state, faction.id, [barracks.id], objective.x, objective.y, objective.id, 'defend'); barracks.rally.strategicPhase = aggressionPhase; }
  }
  const hasLivingHero = state.units.some(unit => unit.faction === faction.id && unit.role === 'hero' && alive(unit));
  const hasQueuedHero = state.buildings.some(building => building.faction === faction.id && (building.queue || []).some(item => item.role === 'hero'));
  if (faction.age >= 3 && barracks && !hasLivingHero && !hasQueuedHero) queueHero(state, faction.id, barracks.id);
  if (!faction.research && faction.age >= 2 && (faction.age < 3 || hasLivingHero || hasQueuedHero)) {
    const priorities = faction.age >= 4
      ? ['master-tools', 'frontier-charters', 'field-armor', 'honed-arms', 'fortified-masonry', 'caravan-ledgers', 'lumen-ballistics', 'heroic-discipline', 'border-citadels', 'lumen-grid', 'concord-logistics', 'everlumen-arms']
      : faction.age >= 3 ? ['master-tools', 'frontier-charters', 'field-armor', 'honed-arms', 'fortified-masonry', 'caravan-ledgers', 'lumen-ballistics', 'heroic-discipline']
        : ['master-tools', 'frontier-charters', 'field-armor', 'honed-arms', 'fortified-masonry'];
    const nextResearch = priorities.find(researchId => canResearchUpgrade(state, faction.id, researchId).ok);
    if (nextResearch) beginResearchUpgrade(state, faction.id, nextResearch);
  }
  if (barracks && barracks.queue.length < 2) queueUnit(state, faction.id, barracks.id, faction.age >= 2 && army.length % 3 === 2 ? 'ranged' : 'melee');
  const desiredSettlements = faction.age >= 4 ? 2 : 1;
  const settlements = state.buildings.filter(item => item.faction === faction.id && item.type === 'settlement' && alive(item));
  if (faction.age >= 2 && settlements.length < desiredSettlements) aiBuild(state, faction, 'settlement', 0.35 + faction.slot * 0.73 + settlements.length * 1.19);
  const completedSettlement = state.buildings.find(item => item.faction === faction.id && item.type === 'settlement' && alive(item) && item.complete);
  if (completedSettlement && !(state.tradeRoutes || []).some(route => route.faction === faction.id && route.active !== false)) {
    const partner = state.buildings.filter(building => building.id !== completedSettlement.id && alive(building) && building.complete && ['heart', 'settlement'].includes(building.type) && areAllies(state, faction.id, building.faction) && distance(building, completedSettlement) >= TRADE_RULES.minimumDistance)
      .sort((a, b) => distance(a, completedSettlement) - distance(b, completedSettlement) || a.id.localeCompare(b.id))[0];
    if (partner) establishTradeRoute(state, faction.id, completedSettlement.id, partner.id);
  }
  const desiredTowers = faction.age >= 4 ? 2 : 1;
  const towers = state.buildings.filter(item => item.faction === faction.id && item.type === 'tower' && alive(item));
  if (state.elapsed > 70 && faction.age >= 2 && towers.length < desiredTowers) aiBuild(state, faction, 'tower', (faction.slot === 1 ? 0.2 : 2.9) + towers.length * 1.03);
  if (completedDepot && faction.age < 5 && !faction.ageResearch && canAdvanceAge(state, faction.id).ok) beginAgeAdvance(state, faction.id);
  if (heart && army.length >= 3 && !army.some(unit => unitHasOrderKind(unit, 'patrol'))) {
    const sentries = army.slice().sort((a, b) => distance(a, heart) - distance(b, heart) || a.id.localeCompare(b.id)).slice(0, 1);
    issuePatrol(state, faction.id, sentries.map(unit => unit.id), heart.x + (faction.slot === 1 ? 125 : -125), heart.y + 70);
  }
  const ownedWell = state.wells.find(well => well.ownerTeamId === faction.teamId);
  const garrisonWell = aggressionPhase === 'forage' || aggressionPhase === 'frontier' ? heart : ownedWell || (heart && state.wells.slice().sort((a, b) => distance(a, heart) - distance(b, heart) || a.id.localeCompare(b.id))[0]);
  if (garrisonWell && army.length >= 3 && !army.some(unit => unitHasOrderKind(unit, 'hold'))) {
    const garrison = army.filter(unit => !unitHasOrderKind(unit, 'patrol')).sort((a, b) => distance(a, garrisonWell) - distance(b, garrisonWell) || a.id.localeCompare(b.id))[0];
    if (garrison) {
      issueMove(state, faction.id, [garrison.id], garrisonWell.x, garrisonWell.y);
      issueHoldPosition(state, faction.id, [garrison.id], { shift: true, x: garrisonWell.x, y: garrisonWell.y });
    }
  }
  const heartThreat = heart && state.units.some(unit => alive(unit) && areEnemies(state, faction.id, unit.faction) && distance(unit, heart) < 260);
  if (heartThreat) {
    const responders = army.filter(unit => ['idle', 'move', 'patrol', 'hold'].includes(unit.order.kind));
    if (responders.length) issueDefend(state, faction.id, responders.map(unit => unit.id), heart.x, heart.y, heart.id, 230);
  }
  if (faction.fortifyClock <= 0) {
    faction.fortifyClock = 34 + faction.slot * 3;
    const candidate = state.buildings.filter(building => building.faction === faction.id && alive(building) && building.complete)
      .sort((a, b) => (a.type === 'heart' ? -1 : a.type === 'tower' ? 0 : 1) - (b.type === 'heart' ? -1 : b.type === 'tower' ? 0 : 1) || (a.fortificationLevel || 0) - (b.fortificationLevel || 0) || a.id.localeCompare(b.id))
      .find(building => canFortifyBuilding(state, faction.id, building.id).ok);
    if (candidate) fortifyBuilding(state, faction.id, candidate.id);
  }
  const defensiveBuilding = state.buildings.find(building => building.faction === faction.id && alive(building) && building.complete && (building.garrisonCapacity || 0) > (building.garrisonIds || []).length && ['tower', 'heart'].includes(building.type));
  if (defensiveBuilding && army.length >= 3 && !(defensiveBuilding.garrisonIds || []).length && !army.some(unit => unit.order?.kind === 'garrison' && unit.order.targetId === defensiveBuilding.id)) {
    const available = army.filter(unit => !unit.expeditionId && unit.order?.kind !== 'garrison');
    const preferred = available.filter(unit => ['idle', 'defend', 'hold'].includes(unit.order.kind));
    const defender = (preferred.length ? preferred : available).sort((a, b) => (['ranged', 'hero'].includes(a.role) ? 0 : 1) - (['ranged', 'hero'].includes(b.role) ? 0 : 1) || distance(a, defensiveBuilding) - distance(b, defensiveBuilding) || a.id.localeCompare(b.id))[0];
    if (defender) issueGarrison(state, faction.id, [defender.id], defensiveBuilding.id);
  }
  const fieldArmy = army.filter(unit => !unitHasOrderKind(unit, 'patrol') && !unitHasOrderKind(unit, 'hold'));
  if (faction.campClock <= 0) {
    faction.campClock = 20 + faction.slot * 3;
    const discovered = state.camps.filter(camp => !camp.cleared && camp.discoveredBy[faction.id] !== undefined)
      .sort((a, b) => distance(heart || units[0], a) - distance(heart || units[0], b) || a.id.localeCompare(b.id))[0];
    const guardian = discovered && discovered.guardianIds.map(guardianId => state.guardians.find(item => item.id === guardianId)).find(alive);
    if (guardian && fieldArmy.length >= 3) issueAttack(state, faction.id, fieldArmy.slice(0, 4).map(unit => unit.id), guardian.id);
    else {
      const unknown = state.camps.filter(camp => !camp.cleared && camp.discoveredBy[faction.id] === undefined)
        .sort((a, b) => distance(heart || units[0], a) - distance(heart || units[0], b) || a.id.localeCompare(b.id))[0];
      const scout = fieldArmy[0] || workers.find(unit => unit.order.kind === 'idle');
      if (unknown && scout) issueScout(state, faction.id, [scout.id], unknown.x, unknown.y, { targetId: unknown.id });
    }
  }
  if (faction.huntClock <= 0) {
    faction.huntClock = 17 + faction.slot * 2;
    const hunter = fieldArmy.find(unit => unit.role !== 'hero') || fieldArmy[0];
    const game = hunter && state.fauna.filter(animal => alive(animal) && factionCurrentlySees(state, faction.id, animal)).sort((a, b) => distance(hunter, a) - distance(hunter, b) || a.id.localeCompare(b.id))[0];
    if (hunter && game && faction.stock.rations < 260) issueAttack(state, faction.id, [hunter.id], game.id);
  }
  if (faction.waveClock <= 0 && fieldArmy.length >= 3 && ['contest', 'war', 'domination'].includes(aggressionPhase)) {
    faction.waveClock = 25 + faction.slot * 4;
    const playerHeart = state.buildings.filter(item => item.type === 'heart' && alive(item) && areEnemies(state, faction.id, item.faction)).sort((a, b) => distance(a, heart) - distance(b, heart) || a.id.localeCompare(b.id))[0];
    const neutralWell = state.wells.find(well => well.ownerTeamId !== faction.teamId);
    const target = ['war', 'domination'].includes(aggressionPhase) && fieldArmy.length >= 7 && playerHeart ? playerHeart : neutralWell || (['war', 'domination'].includes(aggressionPhase) ? playerHeart : null);
    if (target) issueMove(state, faction.id, fieldArmy.map(unit => unit.id), target.x, target.y, true);
  }
}

function updateWells(state, dt) {
  const world = worldOf(state), holdTarget = world.wellHoldSeconds || WELL_HOLD_SECONDS, wellVictoryUnlock = world.wellVictoryUnlock || 0;
  for (const well of state.wells) {
    const contenders = new Map();
    for (const unit of state.units) {
      if (!alive(unit) || unit.insideGarrison || distance(unit, well) > well.radius + 55) continue;
      const faction = factionOf(state, unit.faction); if (!faction || faction.defeated) continue;
      const weight = unit.role === 'worker' ? 1 : unit.role === 'hero' ? 3 : 2;
      const team = contenders.get(faction.teamId) || { weight: 0, factions: new Map() };
      team.weight += weight; team.factions.set(unit.faction, (team.factions.get(unit.faction) || 0) + weight); contenders.set(faction.teamId, team);
    }
    const ranked = [...contenders.entries()].sort((a, b) => b[1].weight - a[1].weight || a[0].localeCompare(b[0]));
    const leader = ranked.length && (!ranked[1] || ranked[0][1].weight > ranked[1][1].weight) ? ranked[0] : null;
    const challengerTeamId = leader?.[0] || null;
    const representative = leader ? [...leader[1].factions.entries()].sort((a, b) => b[1] - a[1] || (factionOf(state, a[0])?.slot || 0) - (factionOf(state, b[0])?.slot || 0) || a[0].localeCompare(b[0]))[0]?.[0] : null;
    // `owner` remains the backwards-compatible faction representative while
    // ownership itself belongs to the team. Re-derive it every tick so old
    // saves and direct scenario setup cannot leave a stale diplomacy value.
    well.ownerTeamId = factionOf(state, well.owner)?.teamId || null;
    if (!challengerTeamId || challengerTeamId === well.ownerTeamId) { well.challenger = null; well.challengerTeamId = null; well.capture = Math.max(0, well.capture - dt * 0.5); }
    else {
      if (well.challengerTeamId !== challengerTeamId) { well.challengerTeamId = challengerTeamId; well.capture = 0; }
      well.challenger = representative;
      const faction = factionOf(state, representative), modifiers = ageModifiers(representative, faction?.age || 1), research = researchModifiers(state, representative);
      well.capture += dt * modifiers.captureRate * research.captureRate;
      if (well.capture >= 10) {
        well.owner = representative; well.ownerTeamId = challengerTeamId; well.capture = 0; well.challenger = null; well.challengerTeamId = null;
        emitEvent(state, 'well-captured', well, { faction: representative, teamId: challengerTeamId });
      }
    }
    const ownerFaction = factionOf(state, well.owner);
    if (ownerFaction && !ownerFaction.defeated) ownerFaction.stock.lumen += dt * 0.24;
  }
  for (const team of state.teams) {
    const owned = state.wells.filter(well => well.ownerTeamId === team.id).length;
    const ageReady = !world.longSession || state.factions.filter(item => item.teamId === team.id && !item.defeated).some(item => item.age >= 4);
    const victoryWindowOpen = state.elapsed >= wellVictoryUnlock && ageReady;
    team.holdSeconds = owned >= 2 && victoryWindowOpen ? team.holdSeconds + dt : Math.max(0, team.holdSeconds - dt * 0.5);
    for (const faction of state.factions.filter(item => item.teamId === team.id)) faction.holdSeconds = team.holdSeconds;
    if (team.holdSeconds >= holdTarget && !state.outcome && !team.defeated) {
      state.winnerTeamId = team.id; state.outcome = team.id === state.playerTeamId ? 'victory' : 'defeat';
      emitEvent(state, 'team-victory', null, { teamId: team.id, reason: 'lumen-wells' });
    }
  }
}

function resolveDeaths(state) {
  const newlyDefeated = [];
  let populationDirty = false;
  for (const building of state.buildings) {
    if (building.hp > 0 || building.deathHandled) continue;
    building.deathHandled = true;
    populationDirty = true;
    for (const unitId of building.garrisonIds || []) { const unit = state.units.find(item => item.id === unitId); if (unit) { exitGarrison(state, unit); unit.hp = Math.max(1, unit.hp * 0.8); idleUnit(state, unit); } }
    building.garrisonIds = [];
    emitEvent(state, 'structure-destroyed', building, { faction: building.faction, buildingType: building.type });
    if (building.type === 'heart') {
      const faction = factionOf(state, building.faction);
      if (faction && !faction.defeated) { faction.defeated = true; newlyDefeated.push(faction); }
    }
  }
  for (const faction of newlyDefeated) {
    for (const unit of state.units) if (unit.faction === faction.id) unit.hp = 0;
    for (const building of state.buildings) if (building.faction === faction.id) building.hp = 0;
    const hostile = areEnemies(state, state.playerFaction, faction.id);
    if (hostile) state.stats.defeated += 1; else if (faction.id !== state.playerFaction) state.stats.alliesLost += 1;
    state.message = `${FACTIONS[faction.id].name} has fallen. ${teamOf(state, faction.id)?.name || 'Their coalition'} fights on.`;
    emitEvent(state, 'faction-defeated', null, { faction: faction.id, teamId: faction.teamId, hostile });
    for (const well of state.wells.filter(item => item.owner === faction.id)) {
      const successor = state.factions.filter(item => item.teamId === faction.teamId && !item.defeated).sort((a, b) => a.slot - b.slot || a.id.localeCompare(b.id))[0];
      well.owner = successor?.id || null; well.ownerTeamId = successor ? faction.teamId : null;
    }
  }
  for (const team of state.teams) {
    const wasDefeated = team.defeated, members = state.factions.filter(faction => faction.teamId === team.id);
    team.defeated = members.length > 0 && members.every(faction => faction.defeated);
    if (team.defeated && !wasDefeated) emitEvent(state, 'team-defeated', null, { teamId: team.id });
  }
  const playerTeam = state.teams.find(team => team.id === state.playerTeamId), enemyTeams = state.teams.filter(team => team.id !== state.playerTeamId);
  if (playerTeam?.defeated) {
    state.outcome = 'defeat'; state.winnerTeamId = enemyTeams.every(team => team.defeated) ? null : enemyTeams.find(team => !team.defeated)?.id || null;
    if (state.winnerTeamId) emitEvent(state, 'team-victory', null, { teamId: state.winnerTeamId, reason: 'elimination' });
  }
  else if (enemyTeams.length && enemyTeams.every(team => team.defeated)) { state.outcome = 'victory'; state.winnerTeamId = state.playerTeamId; emitEvent(state, 'team-victory', null, { teamId: state.playerTeamId, reason: 'elimination' }); }
  state.stats.lost += state.units.filter(unit => areAllies(state, state.playerFaction, unit.faction) && unit.hp <= 0 && !unit.deathCounted).length;
  for (const unit of state.units) if (unit.hp <= 0 && !unit.deathCounted) { unit.deathCounted = true; emitEvent(state, 'unit-lost', unit, { faction: unit.faction, role: unit.role }); }
  if (populationDirty) recalculatePopulation(state);
}

export function tick(state, dt = 1 / 30) {
  if (state.paused || state.outcome) return state;
  dt = Math.min(0.1, Math.max(0, dt)); state.elapsed += dt;
  updateAgeResearch(state, dt); updateResearch(state, dt);
  updateBuildings(state, dt);
  for (const unit of state.units) updateUnit(state, unit, dt);
  updateGuardians(state, dt); updateFauna(state, dt); updateTrade(state, dt); updateKnowledge(state, dt);
  const strategicInterval = worldOf(state).strategicInterval || 0.5;
  state.strategicClock = (state.strategicClock ?? strategicInterval) - dt;
  if (state.strategicClock <= 0) {
    state.strategicClock += strategicInterval; updateExploration(state); updateContacts(state); updateTerritories(state, strategicInterval);
  }
  if (!state.outcome) for (const faction of state.factions) if (faction.ai) runAi(state, faction, dt);
  resolveNeutralDeaths(state); resolveDeaths(state); updateExpeditions(state);
  if (!state.outcome) updateWells(state, dt);
  for (const particle of state.particles) particle.life -= dt;
  state.particles = state.particles.filter(particle => particle.life > 0);
  refreshAssignments(state);
  if (Math.floor(state.elapsed * 2) !== Math.floor((state.elapsed - dt) * 2)) recalculatePopulation(state);
  return state;
}

export function contextOrder(state, factionId, selectionIds, target, point, attackMove = false, options = {}) {
  const ids = Array.isArray(selectionIds) ? selectionIds : [];
  const units = validUnitIds(state, factionId, ids);
  const unitIds = units.map(item => item.id);
  const workerIds = units.filter(item => item.role === 'worker').map(item => item.id);
  const fighterIds = units.filter(item => item.role !== 'worker').map(item => item.id);
  const buildingIds = state.buildings.filter(item => ids.includes(item.id) && item.faction === factionId && alive(item) && item.complete).map(item => item.id);
  const destination = point || target || { x: 0, y: 0 };
  const roleHint = options.role || options.workerJob || point?.role || null;

  if (buildingIds.length) return setRallyPoint(state, factionId, buildingIds, destination.x, destination.y, target?.id || null, roleHint);

  if (roleHint === 'gather' && !target) {
    const wanted = options.resourceType && RESOURCE_TYPES.includes(options.resourceType) ? options.resourceType : null;
    // Clicking empty ground in Gather mode may snap only to a nearby deposit
    // the ordering faction can currently see. It must never discover a remote
    // or fog-hidden same-type node on the villager's behalf.
    const node = state.nodes.filter(item => isResourceNodeHarvestable(state, item) && (!wanted || item.type === wanted)
      && factionCurrentlySees(state, factionId, item) && distance(destination, item) <= Math.max(96, item.radius * 2.5))
      .sort((a, b) => distance(destination, a) - distance(destination, b) || a.id.localeCompare(b.id))[0];
    if (node) return issueGather(state, factionId, workerIds, node.id, options) + issueDefend(state, factionId, fighterIds, node.x, node.y, node.id, options.radius, options);
    return 0;
  }
  if (roleHint === 'build' && !target) {
    const foundation = state.buildings.filter(item => item.faction === factionId && alive(item) && !item.complete)
      .sort((a, b) => distance(destination, a) - distance(destination, b) || a.id.localeCompare(b.id))[0];
    if (foundation) return issueBuild(state, factionId, workerIds, foundation.id, options);
  }
  if (roleHint === 'repair' && !target) {
    const damaged = state.buildings.filter(item => item.faction === factionId && alive(item) && item.complete && item.hp < item.maxHp - 0.01)
      .sort((a, b) => distance(destination, a) - distance(destination, b) || a.id.localeCompare(b.id))[0];
    if (damaged) return issueRepair(state, factionId, workerIds, damaged.id, options);
  }
  if (roleHint === 'defend' || roleHint === 'guard') return issueDefend(state, factionId, unitIds, destination.x, destination.y, target?.id || null, options.radius, options);
  if (roleHint === 'hold') return issueHoldPosition(state, factionId, unitIds, options);
  if (roleHint === 'patrol') return issuePatrol(state, factionId, unitIds, destination.x, destination.y, options);
  if (roleHint === 'scout') return issueScout(state, factionId, unitIds, destination.x, destination.y, { ...options, targetId: target?.id || options.targetId });
  if (roleHint === 'garrison' && target && state.buildings.includes(target)) return issueGarrison(state, factionId, unitIds, target.id, options);

  if (target && state.nodes.includes(target) && isResourceNodeHarvestable(state, target)) {
    return issueGather(state, factionId, workerIds, target.id, options) + issueDefend(state, factionId, fighterIds, target.x, target.y, target.id, options.radius, options);
  }
  if (target?.faction && areEnemies(state, factionId, target.faction)) return issueAttack(state, factionId, unitIds, target.id, options);
  if (target && state.buildings.includes(target) && target.faction === factionId) {
    if (!target.complete) return issueBuild(state, factionId, workerIds, target.id, options) + issueDefend(state, factionId, fighterIds, target.x, target.y, target.id, options.radius, options);
    if (target.hp < target.maxHp - 0.01) return issueRepair(state, factionId, workerIds, target.id, options) + issueDefend(state, factionId, fighterIds, target.x, target.y, target.id, options.radius, options);
    return issueDefend(state, factionId, unitIds, target.x, target.y, target.id, options.radius, options);
  }
  if (areAllies(state, factionId, target?.faction) || (target && state.wells.includes(target))) return issueDefend(state, factionId, unitIds, target.x, target.y, target.id, options.radius, options);
  return issueMove(state, factionId, unitIds, destination.x, destination.y, attackMove, options);
}

export function serializeGame(state) { return JSON.stringify(state); }
export function hydrateGame(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || parsed.schema !== 1 || !FACTIONS[parsed.playerFaction] || !Array.isArray(parsed.factions) || !Array.isArray(parsed.units) || !Array.isArray(parsed.buildings)) return null;
    const knownMaps = [...Object.values(MAP_SIZES), ...Object.values(LEGACY_MAPS)];
    const dimensionMatch = knownMaps.find(item => item.width === parsed.world?.width && item.height === parsed.world?.height);
    const map = dimensionMatch || MAP_SIZES[parsed.mapSize] || LEGACY_MAPS[parsed.mapSize] || LEGACY_MAPS.frontier;
    parsed.mapSize = map.id; parsed.world = { ...map };
    parsed.elapsed = Number.isFinite(parsed.elapsed) ? parsed.elapsed : 0;
    parsed.nodes = Array.isArray(parsed.nodes) ? parsed.nodes : [];
    parsed.wells = Array.isArray(parsed.wells) ? parsed.wells : [];
    parsed.camps = Array.isArray(parsed.camps) ? parsed.camps : [];
    parsed.guardians = Array.isArray(parsed.guardians) ? parsed.guardians : [];
    parsed.fauna = Array.isArray(parsed.fauna) ? parsed.fauna : [];
    parsed.regions = Array.isArray(parsed.regions) ? parsed.regions : createRegions(parsed);
    parsed.landmarks = Array.isArray(parsed.landmarks) && parsed.landmarks.length ? parsed.landmarks : createLandmarks(parsed);
    parsed.influenceZones = Array.isArray(parsed.influenceZones) ? parsed.influenceZones : [];
    parsed.tradeRoutes = Array.isArray(parsed.tradeRoutes) ? parsed.tradeRoutes : [];
    parsed.expeditions = Array.isArray(parsed.expeditions) ? parsed.expeditions : [];
    parsed.events = Array.isArray(parsed.events) ? parsed.events : [];
    parsed.commandLog = Array.isArray(parsed.commandLog) ? parsed.commandLog : [];
    parsed.particles = Array.isArray(parsed.particles) ? parsed.particles : [];
    parsed.stats = { trained: 0, gathered: 0, lost: 0, defeated: 0, alliesLost: 0, campsCleared: 0, treasure: 0, discoveries: 0, regionsDiscovered: 0, hunted: 0, huntRations: 0, tradeIncome: 0, technologies: 0, ...(parsed.stats || {}) };
    parsed.contactState = parsed.contactState && typeof parsed.contactState === 'object' ? { firstContactAt: parsed.contactState.firstContactAt ?? null, pairs: parsed.contactState.pairs && typeof parsed.contactState.pairs === 'object' ? parsed.contactState.pairs : {} } : { firstContactAt: null, pairs: {} };
    parsed.strategicClock = Number.isFinite(parsed.strategicClock) ? Math.max(0, parsed.strategicClock) : parsed.world.strategicInterval || 0.5;
    parsed.commandSequence = Number.isFinite(parsed.commandSequence) ? parsed.commandSequence : parsed.events.length + 1;
    const factionIds = parsed.factions.map(faction => faction.id);
    if (new Set(factionIds).size !== factionIds.length || !factionIds.includes(parsed.playerFaction) || factionIds.some(factionId => !FACTIONS[factionId])) return null;
    // Team selection is derived from the canonical civilization order, not
    // the save's display/slot order. Current saves store factions in their
    // seeded match order, and shuffling that already-shuffled array would
    // silently change allies after loading.
    const canonicalFactionIds = Object.keys(FACTIONS).filter(factionId => factionIds.includes(factionId));
    const assignment = teamAssignment(parsed.playerFaction, parsed.seed || 0, canonicalFactionIds), playerTeamId = 'team-player', enemyTeamId = 'team-enemy';
    const savedTeams = Object.fromEntries((Array.isArray(parsed.teams) ? parsed.teams : []).map(team => [team.id, team]));
    const savedTeamHold = Object.fromEntries(Object.entries(savedTeams).map(([teamId, team]) => [teamId, Number(team.holdSeconds) || 0]));
    parsed.exploration = explorationGrid(parsed.world);
    parsed.playerTeamId = playerTeamId; parsed.enemyTeamId = enemyTeamId;
    parsed.teams = [
      { id: playerTeamId, name: 'Meadow Alliance', memberIds: [parsed.playerFaction, ...assignment.allies], human: true, holdSeconds: savedTeamHold[playerTeamId] || 0, dominationSeconds: Number(savedTeams[playerTeamId]?.dominationSeconds) || 0, territoryCount: 0, territoryShare: 0, defeated: false, exploredCells: normalizedExploredCells(savedTeams[playerTeamId]?.exploredCells, parsed.exploration.maxCells) },
      { id: enemyTeamId, name: 'Opposing Coalition', memberIds: assignment.enemies, human: false, holdSeconds: savedTeamHold[enemyTeamId] || 0, dominationSeconds: Number(savedTeams[enemyTeamId]?.dominationSeconds) || 0, territoryCount: 0, territoryShare: 0, defeated: false, exploredCells: normalizedExploredCells(savedTeams[enemyTeamId]?.exploredCells, parsed.exploration.maxCells) },
    ];
    parsed.winnerTeamId = parsed.teams.some(team => team.id === parsed.winnerTeamId) ? parsed.winnerTeamId : null;
    for (const faction of parsed.factions) {
      faction.teamId = parsed.teams.find(team => team.memberIds.includes(faction.id))?.id || enemyTeamId;
      // v2.2 and older saves only recorded an inseparable team-wide reveal.
      // Do not leak those allied cells into v2.3 player-owned fog. Newer saves
      // preserve each faction's independent history normally.
      faction.exploredCells = normalizedExploredCells(faction.exploredCells, parsed.exploration.maxCells);
      faction.age = clamp(Math.floor(faction.age || 1), 1, 5); faction.ageId = AGES[faction.age].id; faction.ageName = AGES[faction.age].name;
      faction.townHallLevel = faction.age; faction.townHallId = faction.ageId;
      faction.ageResearch = faction.ageResearch && AGES[faction.ageResearch.targetAge] && faction.ageResearch.targetAge === faction.age + 1
        ? { targetAge: faction.ageResearch.targetAge, ageId: AGES[faction.ageResearch.targetAge].id, ageName: AGES[faction.ageResearch.targetAge].name, total: AGES[faction.ageResearch.targetAge].researchTime, remaining: clamp(Number(faction.ageResearch.remaining) || 0, 0, AGES[faction.ageResearch.targetAge].researchTime), startedAt: faction.ageResearch.startedAt ?? parsed.elapsed }
        : null;
      faction.upgrades = Array.isArray(faction.upgrades) ? faction.upgrades : [];
      faction.researches = Array.isArray(faction.researches) ? [...new Set(faction.researches.filter(researchId => RESEARCH[researchId]))] : [];
      faction.technologies = Array.isArray(faction.technologies) ? [...new Set(faction.technologies.filter(technologyId => TECHNOLOGIES[technologyId]))] : [];
      faction.knowledge = Math.max(0, Number(faction.knowledge) || 0); faction.knowledgeRate = Math.max(0, Number(faction.knowledgeRate) || 0);
      faction.technologyProgress = faction.technologyProgress && TECHNOLOGIES[faction.technologyProgress.id] ? faction.technologyProgress : null;
      faction.research = faction.research && RESEARCH[faction.research.id] && !faction.researches.includes(faction.research.id)
        ? { id: faction.research.id, name: RESEARCH[faction.research.id].name, sourceId: faction.research.sourceId || null, sourceType: RESEARCH[faction.research.id].source, total: RESEARCH[faction.research.id].researchTime, remaining: clamp(Number(faction.research.remaining) || 0, 0, RESEARCH[faction.research.id].researchTime), startedAt: faction.research.startedAt ?? parsed.elapsed }
        : null;
      faction.exploredCamps = Array.isArray(faction.exploredCamps) ? faction.exploredCamps : [];
      faction.exploredRegions = Array.isArray(faction.exploredRegions) ? faction.exploredRegions.filter(regionId => parsed.regions.some(region => region.id === regionId)) : [];
      faction.expeditionId ??= null; faction.settlementCount = Math.max(0, Number(faction.settlementCount) || 0); faction.tradeIncome = Math.max(0, Number(faction.tradeIncome) || 0); faction.territoryCount = Math.max(0, Number(faction.territoryCount) || 0);
      faction.aggressionPhase = ['forage', 'frontier', 'contest', 'war', 'domination'].includes(faction.aggressionPhase) ? faction.aggressionPhase : parsed.world.longSession ? 'forage' : 'war';
      faction.stock ||= {}; for (const resource of RESOURCE_TYPES) faction.stock[resource] = Number.isFinite(faction.stock[resource]) ? faction.stock[resource] : 0;
      faction.campClock = Number.isFinite(faction.campClock) ? faction.campClock : 18 + (faction.slot || 0) * 4;
      faction.huntClock = Number.isFinite(faction.huntClock) ? faction.huntClock : 13 + (faction.slot || 0) * 3;
      faction.fortifyClock = Number.isFinite(faction.fortifyClock) ? faction.fortifyClock : 45 + (faction.slot || 0) * 5;
    }
    for (const team of parsed.teams) {
      const members = parsed.factions.filter(faction => faction.teamId === team.id); team.defeated = members.length > 0 && members.every(faction => faction.defeated);
      if (!savedTeamHold[team.id]) team.holdSeconds = Math.max(0, ...members.map(faction => Number(faction.holdSeconds) || 0));
    }
    for (const unit of parsed.units) {
      unit.order = unit.order && typeof unit.order.kind === 'string' ? unit.order : { kind: 'idle' };
      const jobKind = unit.order.kind === 'move' ? 'travel' : ['gather', 'build', 'repair', 'defend', 'hold', 'patrol', 'attack', 'scout', 'garrison'].includes(unit.order.kind) ? unit.order.kind : 'idle';
      unit.job = unit.job || { kind: jobKind, targetId: unit.order.targetId || null, resourceType: unit.order.resourceType || null, assignedAt: parsed.elapsed || 0 };
      const legacyActivity = { travel: 'moving', traveling: 'moving', 'traveling-to-resource': 'moving', 'traveling-to-build': 'moving', 'traveling-to-repair': 'moving', constructing: 'building', pursuing: 'moving', engaging: 'attacking', advancing: 'moving', 'taking-position': 'moving', 'returning-to-post': 'moving' };
      unit.activity = legacyActivity[unit.activity] || unit.activity || initialActivity(unit.order);
      unit.activityStartedAt ??= parsed.elapsed || 0;
      unit.orderIssuedAt ??= parsed.elapsed || 0;
      unit.orderQueue = Array.isArray(unit.orderQueue) ? unit.orderQueue.filter(item => item?.order && typeof item.order.kind === 'string').map(item => ({ order: cloneOrder(item.order), jobKind: item.jobKind || item.order.kind })) : [];
      unit.worksiteId ??= null; unit.workSlot ??= null; unit.insideWorksite = Boolean(unit.insideWorksite && unit.worksiteId && unit.workSlot);
      if (unit.order.kind === 'build') unit.order.phase = unit.insideWorksite ? 'work' : 'travel';
      unit.stance ||= unit.order.kind === 'hold' ? 'hold' : ['patrol', 'defend'].includes(unit.order.kind) ? 'defensive' : 'responsive';
      if (unit.order.kind === 'idle') { unit.insideWorksite = false; unit.worksiteId = null; unit.workSlot = null; }
      else if (['gather', 'build', 'repair'].includes(unit.order.kind) && (!unit.worksiteId || !unit.workSlot)) attachWorksite(parsed, unit, unit.order);
      unit.job.phase = unit.activity;
      unit.lastHitAt ??= null; unit.lastAttackerId ??= null;
      unit.facing ??= 0;
      unit.armor = Number(unit.armor) || 0; unit.shieldUntil = Number(unit.shieldUntil) || 0; unit.shieldArmor = Number(unit.shieldArmor) || 0;
      unit.weakenedUntil = Number(unit.weakenedUntil) || 0; unit.inspiredUntil = Number(unit.inspiredUntil) || 0;
      unit.garrisonId ??= null; unit.insideGarrison = Boolean(unit.insideGarrison && unit.garrisonId);
      if (unit.role === 'hero') {
        const hero = HEROES[unit.faction], profile = unitProfile(unit.faction, 'hero', factionOf(parsed, unit.faction)?.age || 3);
        if (!hero || !profile) return null;
        unit.type = 'hero'; unit.isHero = true; unit.heroId ||= hero.id; unit.name ||= hero.name; unit.title ||= hero.title;
        unit.level = clamp(Math.floor(unit.level || 1), 1, 5); unit.experience = Math.max(0, Number(unit.experience) || 0); unit.nextLevelXp = Number(unit.nextLevelXp) || unit.level * 60;
        unit.ability = { ...hero.ability, remaining: 0, lastUsedAt: null, ...(unit.ability || {}) };
        unit.maxHp ||= profile.hp; unit.hp = Number.isFinite(unit.hp) ? unit.hp : unit.maxHp; unit.damage ||= profile.damage;
        unit.expeditionId ??= null; unit.expeditionStage ??= null; unit.expeditionStatus ??= null;
      }
    }
    parsed.buildings.forEach((building, index) => {
      building.queue = Array.isArray(building.queue) ? building.queue : [];
      building.queue.forEach((item, queueIndex) => { item.id ||= `q-legacy-${index}-${queueIndex}`; item.queuedAt ??= parsed.elapsed || 0; });
      if (building.rally && !building.rally.kind) building.rally = { kind: 'point', targetId: null, setAt: 0, ...building.rally };
      building.activity ||= building.complete ? (building.queue.length ? 'training' : 'ready') : 'foundation';
      building.assignedWorkers ??= 0; building.lastSpawnId ??= null; building.lastHitAt ??= null; building.lastAttackerId ??= null;
      building.fortificationLevel = clamp(Math.floor(building.fortificationLevel || 0), 0, 4); building.fortified = building.fortificationLevel > 0;
      building.fortificationName = building.fortificationLevel ? FORTIFICATION_LEVELS[building.fortificationLevel].name : null;
      const profile = effectiveBuildingProfile(parsed, building.faction, building.type, building.fortificationLevel);
      building.armor = Number.isFinite(building.armor) ? building.armor : profile.armor; building.garrisonIds = Array.isArray(building.garrisonIds) ? building.garrisonIds : [];
      building.garrisonCapacity = Number.isFinite(building.garrisonCapacity) ? building.garrisonCapacity : building.type === 'heart' ? 4 : building.type === 'settlement' ? 3 : building.type === 'tower' || building.type === 'depot' ? 2 : 0;
      building.defenseCooldown = Number(building.defenseCooldown) || 0; building.researching = parsed.factions.find(faction => faction.research?.sourceId === building.id)?.research?.id || null;
      building.shieldUntil = Number(building.shieldUntil) || 0; building.shieldArmor = Number(building.shieldArmor) || 0;
      const baseInfluence = parsed.world.settlementInfluence || 500;
      building.influenceRadius = Number(building.influenceRadius) || (building.type === 'heart' ? baseInfluence * 1.12 : building.type === 'settlement' ? baseInfluence : 0);
      building.knowledgeRate = Number(building.knowledgeRate) || (building.type === 'heart' ? 0.018 : building.type === 'settlement' ? 0.045 : 0);
    });
    for (const node of parsed.nodes || []) {
      node.assignedWorkers ??= 0;
      // Older saves keep their exact deposits and amounts while adopting the
      // larger current interaction footprint.
      node.radius = Math.max(Number(node.radius) || 0, RESOURCE_NODE_RADII[node.type] || 32);
      if (node.enclaveId) { node.depositClass = 'resource-enclave'; node.guarded = node.guarded !== false; }
    }
    for (const well of parsed.wells) {
      if (!factionOf(parsed, well.owner)) well.owner = null; if (!factionOf(parsed, well.challenger)) well.challenger = null;
      well.ownerTeamId = factionOf(parsed, well.owner)?.teamId || null; well.challengerTeamId = factionOf(parsed, well.challenger)?.teamId || null;
    }
    for (const camp of parsed.camps) {
      camp.type = 'neutral-camp'; camp.campKind = camp.campKind === 'resource-enclave' ? 'resource-enclave' : 'treasure-camp'; camp.guardianIds = Array.isArray(camp.guardianIds) ? camp.guardianIds : [];
      if (resourceEnclaveCamp(camp)) { camp.faction = 'neutral'; camp.teamId = null; camp.owner = null; camp.originSlot = Math.max(0, Math.floor(Number(camp.originSlot) || 0)); }
      camp.resourceNodeIds = resourceEnclaveCamp(camp) && Array.isArray(camp.resourceNodeIds)
        ? camp.resourceNodeIds.filter(nodeId => parsed.nodes.some(node => node.id === nodeId && node.enclaveId === camp.id)) : [];
      camp.treasure = resourceEnclaveCamp(camp) ? {} : camp.treasure || {};
      camp.treasureRemaining = resourceEnclaveCamp(camp) ? false : camp.treasureRemaining !== false && !camp.cleared;
      camp.cleared = Boolean(camp.cleared); camp.clearedAt ??= null; camp.rewardFaction ??= null; camp.discoveredBy ||= {}; camp.discoveredByTeams ||= {};
      for (const [factionId, discoveredAt] of Object.entries(camp.discoveredBy)) { const teamId = factionOf(parsed, factionId)?.teamId; if (teamId && camp.discoveredByTeams[teamId] === undefined) camp.discoveredByTeams[teamId] = discoveredAt; }
      camp.aggroFaction ??= null; camp.aggroTeamId = factionOf(parsed, camp.aggroFaction)?.teamId || null; camp.rewardTeamId = factionOf(parsed, camp.rewardFaction)?.teamId || null;
      camp.activity ||= camp.cleared ? 'cleared' : resourceEnclaveCamp(camp) ? 'resource-guarded' : 'guarded';
    }
    for (const guardian of parsed.guardians) {
      const profile = GUARDIAN_PROFILES[guardian.guardianRole] || GUARDIAN_PROFILES.prowler;
      guardian.type = 'guardian'; guardian.faction = 'neutral'; guardian.role = 'guardian'; guardian.guardianRole = profile.role;
      guardian.name ||= profile.name; guardian.radius ||= profile.radius; guardian.maxHp ||= profile.hp; guardian.hp = Number.isFinite(guardian.hp) ? guardian.hp : guardian.maxHp;
      guardian.speed ||= profile.speed; guardian.damage ||= profile.damage; guardian.range ||= profile.range; guardian.attackCooldown ||= profile.cooldown; guardian.sight ||= profile.sight;
      guardian.cooldown = Number(guardian.cooldown) || 0; guardian.order ||= { kind: 'guard', targetId: null }; guardian.activity ||= alive(guardian) ? 'guarding' : 'defeated';
      guardian.activityStartedAt ??= parsed.elapsed; guardian.kills = Number(guardian.kills) || 0; guardian.lastHitAt ??= null; guardian.lastAttackerId ??= null; guardian.deathHandled = Boolean(guardian.deathHandled);
    }
    for (const node of parsed.nodes.filter(item => item.enclaveId)) node.guarded = !isResourceNodeHarvestable(parsed, node);
    for (const animal of parsed.fauna) {
      const profile = FAUNA_PROFILES[animal.species]; if (!profile) continue;
      animal.type = 'fauna'; animal.faction = 'neutral'; animal.faunaRole = profile.faunaRole; animal.name ||= profile.name;
      animal.radius ||= profile.radius; animal.maxHp ||= profile.hp; animal.hp = Number.isFinite(animal.hp) ? animal.hp : animal.maxHp;
      animal.speed ||= profile.speed; animal.sight ||= profile.sight; animal.damage ??= profile.damage || 0; animal.range ??= profile.range || 0; animal.attackCooldown ??= profile.cooldown || 0;
      animal.rationYield ||= profile.rations; animal.cooldown = Number(animal.cooldown) || 0; animal.activity ||= alive(animal) ? 'grazing' : 'fallen'; animal.activityStartedAt ??= parsed.elapsed;
      animal.order ||= { kind: 'graze' }; animal.homeX ??= animal.x; animal.homeY ??= animal.y; animal.waypointAngle ??= 0; animal.facing ??= 0;
      animal.grazeUntil = Number.isFinite(animal.grazeUntil) ? animal.grazeUntil : parsed.elapsed + 2.2; animal.fleeUntil = Number(animal.fleeUntil) || 0; animal.fleeFromX = Number.isFinite(animal.fleeFromX) ? animal.fleeFromX : null; animal.fleeFromY = Number.isFinite(animal.fleeFromY) ? animal.fleeFromY : null;
      animal.kills = Number(animal.kills) || 0; animal.lastHitAt ??= null; animal.lastAttackerId ??= null; animal.deathHandled = Boolean(animal.deathHandled);
      animal.huntedByFaction = factionOf(parsed, animal.huntedByFaction)?.id || null; animal.huntedByTeamId = factionOf(parsed, animal.huntedByFaction)?.teamId || null; animal.huntedAt ??= null;
    }
    for (const landmark of parsed.landmarks) {
      landmark.type = 'landmark'; landmark.radius = Number(landmark.radius) || (parsed.world.longSession ? 72 : 44); landmark.discoveredBy ||= {}; landmark.discoveredByTeams ||= {}; landmark.activity ||= Object.keys(landmark.discoveredBy).length ? 'discovered' : 'unexplored';
      landmark.x = clamp(Number(landmark.x) || parsed.world.width / 2, 0, parsed.world.width); landmark.y = clamp(Number(landmark.y) || parsed.world.height / 2, 0, parsed.world.height);
    }
    for (const region of parsed.regions) {
      region.type = 'territory-region'; region.discoveredBy ||= {}; region.discoveredByTeams ||= {}; region.influence ||= {}; region.controllingTeamId = parsed.teams.some(team => team.id === region.controllingTeamId) ? region.controllingTeamId : null;
      region.controllingFaction = factionOf(parsed, region.controllingFaction)?.id || null; region.contested = Boolean(region.contested); region.controlledAt ??= null;
    }
    parsed.tradeRoutes = parsed.tradeRoutes.filter(route => route && factionOf(parsed, route.faction) && parsed.buildings.some(building => building.id === route.sourceId) && parsed.buildings.some(building => building.id === route.targetId)).map(route => ({
      ...route, type: 'trade-route', teamId: factionOf(parsed, route.faction).teamId, partnerFaction: factionOf(parsed, route.partnerFaction)?.id || parsed.buildings.find(building => building.id === route.targetId)?.faction || route.faction,
      distance: Math.max(0, Number(route.distance) || distance(parsed.buildings.find(building => building.id === route.sourceId), parsed.buildings.find(building => building.id === route.targetId))),
      cycleSeconds: Number(route.cycleSeconds) || TRADE_RULES.cycleSeconds, progress: clamp(Number(route.progress) || 0, 0, Number(route.cycleSeconds) || TRADE_RULES.cycleSeconds),
      deliveries: Math.max(0, Math.floor(route.deliveries || 0)), totalIncome: Math.max(0, Number(route.totalIncome) || 0), active: route.active !== false,
    }));
    parsed.expeditions = parsed.expeditions.filter(expedition => expedition && EXPEDITION_QUESTS[expedition.questId] && factionOf(parsed, expedition.faction) && parsed.units.some(unit => unit.id === expedition.heroUnitId && unit.role === 'hero')).map(expedition => {
      const stages = Array.isArray(expedition.stages) ? expedition.stages.filter(stage => EXPEDITION_QUESTS[expedition.questId].stages.includes(stage.kind)).map(stage => ({ ...stage, complete: Boolean(stage.complete) })) : [];
      const stageIndex = clamp(Math.floor(expedition.stageIndex || 0), 0, stages.length);
      const status = ['active', 'complete', 'cancelled', 'failed'].includes(expedition.status) ? expedition.status : 'failed';
      return { ...expedition, type: 'hero-expedition', teamId: factionOf(parsed, expedition.faction).teamId, status: status === 'active' && !stages.length ? 'failed' : status, stages, stageIndex, currentStage: stages[stageIndex] || null, reward: { ...EXPEDITION_QUESTS[expedition.questId].reward, ...(expedition.reward || {}) } };
    });
    for (const faction of parsed.factions) {
      const expedition = parsed.expeditions.find(item => item.id === faction.expeditionId && item.faction === faction.id && item.status === 'active'); faction.expeditionId = expedition?.id || null;
    }
    for (const unit of parsed.units.filter(item => item.role === 'hero')) {
      const expedition = parsed.expeditions.find(item => item.id === unit.expeditionId && item.heroUnitId === unit.id && item.status === 'active');
      unit.expeditionId = expedition?.id || null; unit.expeditionStage = expedition?.currentStage?.kind || null; if (expedition) unit.expeditionStatus = 'active';
    }
    for (const building of parsed.buildings) {
      building.garrisonIds = building.garrisonIds.filter(unitId => parsed.units.some(unit => unit.id === unitId && alive(unit) && unit.garrisonId === building.id));
      if (building.rally?.kind === 'attack') { const target = getEntity(parsed, building.rally.targetId); if (target && !areEnemies(parsed, building.faction, target.faction)) building.rally = { ...building.rally, kind: 'defend' }; }
    }
    for (const unit of parsed.units) {
      if (unit.insideGarrison && !parsed.buildings.some(building => building.id === unit.garrisonId && alive(building) && building.garrisonIds.includes(unit.id))) { unit.insideGarrison = false; unit.garrisonId = null; }
      if (unit.order.kind === 'attack') { const target = getEntity(parsed, unit.order.targetId); if (target && !areEnemies(parsed, unit.faction, target.faction)) idleUnit(parsed, unit); }
      unit.orderQueue = unit.orderQueue.filter(entry => { if (entry.order.kind !== 'attack') return true; const target = getEntity(parsed, entry.order.targetId); return !target || areEnemies(parsed, unit.faction, target.faction); });
    }
    refreshAssignments(parsed); recalculatePopulation(parsed); updateExploredCells(parsed);
    return parsed;
  } catch { return null; }
}

export function objectiveRules(state) {
  const world = worldOf(state);
  return {
    wellHoldSeconds: world.wellHoldSeconds || WELL_HOLD_SECONDS,
    wellVictoryUnlock: world.wellVictoryUnlock || 0,
    dominationUnlock: world.dominationUnlock || null,
    dominationShare: world.dominationShare || null,
    dominationHoldSeconds: world.dominationHoldSeconds || null,
    contactDelay: world.contactDelay || 0,
  };
}

export const RULES = Object.freeze({ wellHoldSeconds: WELL_HOLD_SECONDS });
